import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { moderateText } from '../../lib/moderationService.js';
import WebTopBar from '../components/WebTopBar.js';
import { DCard, BtnGold } from '../components/MarketplaceAtoms.js';
import AdminNoticiasPanel from './AdminNoticiasPanel.js';
import AdminComunidadPanel from './AdminComunidadPanel.js';
import AdminSocialKitModal from '../components/AdminSocialKitModal.jsx';
import AdminListingSocialActions from '../components/AdminListingSocialActions.jsx';
import AdminAdCampaignForm from '../components/AdminAdCampaignForm.jsx';
import AdminAdSlotsOverview from '../components/AdminAdSlotsOverview.jsx';
import { getAdSlotLabel } from '../../lib/adSlotsConfig.js';
import {
  deleteAdminAd,
  duplicateAdminAd,
  updateAdminAdSortOrder,
} from '../../lib/adminAdsService.js';
import { isAdVisibleOnSiteNow } from '../../lib/adsService.js';
import { fetchSocialNetworksByContentIds } from '../../lib/adminListingSocial.js';
import { adminSetListingStatus } from '../../lib/adminListingModeration.js';
import { fetchPublicListingById } from '../../lib/listingsService.js';
import AdminUsuariosPanel from './AdminUsuariosPanel.js';
import AdminSorteosPanel from './AdminSorteosPanel.js';
import AdminSocialQueuePanel from './AdminSocialQueuePanel.js';
import { PARTNER_ROLE_LABELS as ROLE_LABELS } from '../constants/partnerRoleDefs.js';
import {
  normalizePartnerScheduleValue,
  PARTNER_SCHEDULE_OPTIONS,
  roleNeedsPartnerSchedule,
} from '../constants/partnerSchedule.js';
import { profileGroovesBalance } from '../constants/groovesEconomy.js';
import {
  ADMIN_ALERT_DOT_STYLE,
  adminMenuBadgeCount,
  adminMenuBadgeTotal,
  adminMenuItemBadgeCount,
  adminMenuGroupBadgeSum,
  adminReportesTabBadges,
  emptyAdminMenuBadges,
  fetchAdminMenuBadgeCounts,
} from '../../lib/adminMenuBadges.js';
import { ADMIN_SEG } from '../../lib/adminSeguimientoLecturas.js';
import {
  useAdminSeguimiento,
  AdminLeidaCell,
  AdminSeguimientoMarcarTodas,
  AdminSeguimientoTabLabel,
  adminSeguimientoRowStyle,
} from '../components/AdminSeguimientoLeido.js';
import {
  SECTION_MAINT_KEYS,
  MAINTENANCE_LOG_SECTIONS,
  MAINTENANCE_LOG_ACCIONES,
  buildMaintenancePayload,
  maintenanceDatetimeLocalToIso,
  maintenanceFinEstimadoToDatetimeLocal,
  maintenanceSectionLabel,
  parseMaintenanceConfig,
} from '../utils/maintenanceConfig.js';
import { invalidatePartnerTicketSalesConfigCache } from '../../lib/partnerTicketSales.js';

const SECTOR_LABELS = {
  tienda_discos: 'Disquería / discos',
  comercio_online: 'Comercio online',
  sello: 'Sello',
  festival: 'Festival',
  audio: 'Audio',
  editorial: 'Editorial',
  streaming: 'Streaming',
  artista: 'Artista',
  venue: 'Venue',
  otro: 'Otro',
};

const PARTNER_TABS = [
  { id: 'pending', label: 'Pendientes' },
  { id: 'reviewing', label: 'En revisión' },
  { id: 'approved', label: 'Aprobados' },
  { id: 'rejected', label: 'Rechazados' },
  { id: 'roles_pending', label: 'Roles pendientes' },
];

const MENU_GROUPS = [
  {
    id: 'grp_inicio',
    title: 'Inicio',
    emoji: '🏠',
    items: [{ id: 'dashboard', label: 'Dashboard', hint: 'Resumen y métricas del momento.' }],
  },
  {
    id: 'grp_partners',
    title: 'Partners',
    emoji: '🤝',
    items: [
      {
        id: 'partners',
        label: 'Partners',
        hint: 'Alta y revisión de partners. Los cambios de rol del panel Partner están en «Roles pendientes».',
      },
      { id: 'admin_mensajes', label: 'Mensajes Partner', hint: 'Hilos soporte y mensajes con partners.' },
      { id: 'directorio', label: 'Directorio Partners', hint: 'Ficha pública y directorio de partners.' },
      { id: 'partner_registro', label: 'Registro partner', hint: 'Solicitudes de registro partner.' },
    ],
  },
  {
    id: 'grp_marketplace',
    title: 'Marketplace',
    emoji: '🛒',
    items: [
      { id: 'valoraciones', label: 'Valoraciones', hint: 'Reviews y visibilidad.' },
      { id: 'subastas', label: 'Subastas', hint: 'Gestión de subastas y kit redes.' },
      { id: 'transacciones', label: 'Transacciones', hint: 'Ventas, comisiones y envíos.' },
    ],
  },
  {
    id: 'grp_usuarios',
    title: 'Usuarios',
    emoji: '👥',
    items: [
      { id: 'usuarios', label: 'Usuarios', hint: 'Perfiles, estado de cuenta y moderación.' },
      { id: 'reportes', label: 'Reportes', hint: 'Trust & safety: reportes abiertos.' },
      { id: 'notificaciones', label: 'Notificaciones', hint: 'Comunicados masivos e historial.' },
      { id: 'grooves', label: 'Grooves', hint: 'Ledger, top y concesión manual.' },
    ],
  },
  {
    id: 'grp_contenido',
    title: 'Contenido',
    emoji: '📢',
    items: [
      { id: 'noticias', label: 'Noticias', hint: 'Borradores, publicar y kit redes.' },
      { id: 'comunidad', label: 'Comunidad', hint: 'Pendientes de usuarios, borradores n8n, feed y kit redes.' },
      { id: 'listings', label: 'Mercado', hint: 'Anuncios activos, moderación y publicación en redes (como Noticias/Comunidad).' },
      { id: 'eventos', label: 'Eventos', hint: 'Eventos y estados.' },
      { id: 'sorteos', label: 'Sorteos', hint: 'Sorteos, premios e inscripciones.' },
      { id: 'publicidad', label: 'Publicidad', hint: 'Anuncios y revisiones.' },
      { id: 'redes_cola', label: 'Cola redes', hint: 'Cola social_publish_queue y URLs publicadas.' },
    ],
  },
  {
    id: 'grp_datos',
    title: 'Datos',
    emoji: '📊',
    items: [
      { id: 'estadisticas', label: 'Estadísticas', hint: 'Analítica del sitio.' },
      { id: 'emails', label: 'Emails', hint: 'Cola y estado de envíos.' },
      { id: 'auditoria', label: 'Auditoría', hint: 'Registro de actividad admin.' },
    ],
  },
  {
    id: 'grp_sistema',
    title: 'Sistema',
    emoji: '⚙️',
    items: [
      { id: 'maintenance', label: 'Mantenimiento', hint: 'Modo mantenimiento por sección.' },
      { id: 'config', label: 'Configuración', hint: 'Parámetros globales de la app.' },
    ],
  },
];

const MENU_FLAT = MENU_GROUPS.flatMap(g => g.items);

/** Punto rojo de aviso (menú lateral y cabecera de sección). */
function AdminAlertDot({ title, size = 8 }) {
  return (
    <span
      title={title || 'Hay avisos pendientes'}
      aria-hidden
      style={{
        ...ADMIN_ALERT_DOT_STYLE,
        width: size,
        height: size,
      }}
    />
  );
}

/** Pastilla numérica opcional junto al punto rojo. */
function AdminAlertCount({ count }) {
  const n = Math.max(0, Number(count) || 0);
  if (n < 1) return null;
  return (
    <span
      style={{
        minWidth: 18,
        padding: '2px 6px',
        borderRadius: 999,
        background: 'rgba(226,75,74,0.95)',
        color: '#0a0a0f',
        fontSize: 10,
        fontWeight: 900,
        lineHeight: 1.2,
        textAlign: 'center',
        flexShrink: 0,
      }}
      title={`${n} pendiente${n === 1 ? '' : 's'}`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

const btnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

function AdminGroovesPanel({ user, onError, onBadgesRefresh }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [topUsers, setTopUsers] = useState([]);
  const [rankingSemana, setRankingSemana] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [sumWeek, setSumWeek] = useState(0);
  const [sumMonth, setSumMonth] = useState(0);
  const [grantProfile, setGrantProfile] = useState('');
  const [grantDelta, setGrantDelta] = useState('50');
  const [grantNote, setGrantNote] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);
  const [weeklyResetBusy, setWeeklyResetBusy] = useState(false);
  const [devBuyer, setDevBuyer] = useState('');
  const [devSeller, setDevSeller] = useState('');
  const [devTx, setDevTx] = useState('');
  const [devAmt, setDevAmt] = useState('50');
  const [devNote, setDevNote] = useState('');
  const [devBusy, setDevBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const q = query.trim();
      let req = supabase
        .from('grooves_ledger')
        .select('id, profile_id, delta, reason, idempotency_key, ref_type, ref_id, created_at')
        .order('created_at', { ascending: false })
        .limit(80);
      if (q) req = req.eq('profile_id', q);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const [ledgerRes, topGroovesRes, topTotalRes, wRes, mRes, rkRes, chRes] = await Promise.all([
        req,
        supabase.from('profiles').select('id, username, grooves, grooves_total').order('grooves', { ascending: false }).limit(25),
        supabase.from('profiles').select('id, username, grooves, grooves_total').order('grooves_total', { ascending: false }).limit(25),
        supabase.from('grooves_ledger').select('delta, created_at').gte('created_at', weekAgo).gt('delta', 0).limit(8000),
        supabase.from('grooves_ledger').select('delta, created_at').gte('created_at', monthAgo).gt('delta', 0).limit(8000),
        supabase.rpc('list_grooves_ranking_semana_activo', { p_limit: 30 }),
        supabase.from('grooves_level_challenges').select('*').order('sort_order', { ascending: true }),
      ]);
      if (ledgerRes.error) throw ledgerRes.error;
      if (topGroovesRes.error) throw topGroovesRes.error;
      if (topTotalRes.error) throw topTotalRes.error;
      setRows(ledgerRes.data ?? []);
      const topMap = new Map();
      for (const r of [...(topGroovesRes.data ?? []), ...(topTotalRes.data ?? [])]) {
        topMap.set(r.id, r);
      }
      const mergedTop = [...topMap.values()]
        .sort((a, b) => profileGroovesBalance(b) - profileGroovesBalance(a))
        .slice(0, 10);
      setTopUsers(mergedTop);
      if (rkRes.error) console.warn('[AdminGrooves] ranking rpc:', rkRes.error.message);
      setRankingSemana(Array.isArray(rkRes.data) ? rkRes.data : []);
      if (chRes.error) console.warn('[AdminGrooves] challenges:', chRes.error.message);
      setChallenges(Array.isArray(chRes.data) ? chRes.data : []);
      setSumWeek((wRes.data ?? []).reduce((a, r) => a + (Number(r.delta) || 0), 0));
      setSumMonth((mRes.data ?? []).reduce((a, r) => a + (Number(r.delta) || 0), 0));
    } catch (e) {
      onError(e?.message || 'No se pudo cargar Grooves ledger');
      setRows([]);
      setTopUsers([]);
      setRankingSemana([]);
      setChallenges([]);
      setSumWeek(0);
      setSumMonth(0);
    } finally {
      setLoading(false);
    }
  }, [onError, query]);

  useEffect(() => {
    load();
  }, [load]);

  const grantGrooves = async () => {
    const pid = grantProfile.trim();
    const d = Math.trunc(Number(grantDelta));
    if (!pid || !/^[0-9a-f-]{36}$/i.test(pid)) {
      onError('Indica un profile_id UUID válido');
      return;
    }
    if (!Number.isFinite(d) || d < 1 || d > 5000) {
      onError('Cantidad: entero entre 1 y 5000');
      return;
    }
    setGrantBusy(true);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'admin_grant_grooves',
          payload: { profile_id: pid, delta: d, note: grantNote.trim() || 'Premio / corrección manual' },
        },
      });
      if (error || !data?.ok) throw error || new Error(data?.message || 'No se pudo conceder');
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'admin_grooves_granted_ui',
        entity_type: 'profile',
        entity_id: pid,
        summary: `UI: grooves +${d}`,
        metadata: { delta: d },
      });
      setGrantNote('');
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo conceder grooves');
    } finally {
      setGrantBusy(false);
    }
  };

  const runWeeklyRankingReset = async () => {
    setWeeklyResetBusy(true);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'grooves_weekly_ranking_reset', payload: {} },
      });
      if (error || !data?.ok) throw error || new Error(data?.message || 'RPC falló');
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo ejecutar el cierre semanal');
    } finally {
      setWeeklyResetBusy(false);
    }
  };

  const submitDevolucionGrooves = async () => {
    const buyer = devBuyer.trim();
    const seller = devSeller.trim();
    const tx = devTx.trim();
    const amt = Math.trunc(Number(devAmt));
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!buyer || !seller || !tx || !uuidRe.test(buyer) || !uuidRe.test(seller) || !uuidRe.test(tx)) {
      onError('UUID comprador, vendedor y transacción obligatorios.');
      return;
    }
    if (!Number.isFinite(amt) || amt < 1) {
      onError('Cantidad inválida.');
      return;
    }
    setDevBusy(true);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'admin_grooves_devolucion_incidencia',
          payload: {
            buyer_profile_id: buyer,
            seller_profile_id: seller,
            transaction_id: tx,
            amount: amt,
            note: devNote.trim(),
          },
        },
      });
      if (error || !data?.ok) throw error || new Error(data?.message || 'No aplicado');
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'admin_grooves_devolucion_ui',
        entity_type: 'transaction',
        entity_id: tx,
        summary: `Devolución grooves UI ±${amt}`,
        metadata: { buyer, seller, incident_id: data.incident_id },
      });
      setDevNote('');
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo registrar la devolución');
    } finally {
      setDevBusy(false);
    }
  };

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 10, lineHeight: 1.5 }}>
          <strong style={{ color: C.gold }}>Resumen:</strong> grooves positivos concedidos en los últimos 7 días:{' '}
          <strong style={{ color: '#fff' }}>{sumWeek}</strong> · últimos 30 días: <strong style={{ color: '#fff' }}>{sumMonth}</strong>
        </div>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Top 10 por saldo (GREATEST de grooves / grooves_total)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {topUsers.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setGrantProfile(p.id)}
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: 11,
                cursor: 'pointer',
              }}
              title="Usar este UUID en el formulario de concesión"
            >
              @{p.username || p.id.slice(0, 8)} — <span style={{ color: C.gold }}>{profileGroovesBalance(p)}</span>
            </button>
          ))}
          {!topUsers.length ? <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sin datos.</span> : null}
        </div>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Conceder grooves manualmente</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>profile_id</div>
            <input
              value={grantProfile}
              onChange={e => setGrantProfile(e.target.value)}
              placeholder="UUID del usuario"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ width: 100 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Cantidad</div>
            <input
              value={grantDelta}
              onChange={e => setGrantDelta(e.target.value)}
              type="number"
              min={1}
              max={5000}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Nota (auditoría)</div>
            <input
              value={grantNote}
              onChange={e => setGrantNote(e.target.value)}
              placeholder="Premio, corrección…"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <BtnGold type="button" style={{ width: 'auto', minWidth: 140 }} disabled={grantBusy} onClick={grantGrooves}>
            {grantBusy ? 'Aplicando…' : 'Conceder'}
          </BtnGold>
        </div>
      </DCard>

      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Ranking semanal (grooves_semana)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, lineHeight: 1.5 }}>
          Posiciones en vivo. El cierre oficial agrega la semana ISO anterior desde el ledger y otorga insignias top 3.
          Programa <code style={{ color: C.gold }}>grooves_cerrar_semana_ranking</code> con pg_cron o ejecuta aquí en pruebas.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {rankingSemana.slice(0, 15).map(r => (
            <div
              key={r.profile_id}
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.1)',
                fontSize: 11,
                color: '#fff',
              }}>
              #{r.rank_pos} @{r.username || r.profile_id?.slice(0, 8)} —{' '}
              <span style={{ color: C.gold }}>{r.grooves_semana}</span>
            </div>
          ))}
          {!rankingSemana.length ? <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sin grooves esta semana.</span> : null}
        </div>
        <BtnGold type="button" style={{ width: 'auto', minWidth: 220 }} disabled={weeklyResetBusy} onClick={runWeeklyRankingReset}>
          {weeklyResetBusy ? 'Ejecutando…' : 'Cerrar semana y repartir insignias (RPC)'}
        </BtnGold>
      </DCard>

      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Retos por nivel (catálogo)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
          Gestión en tabla <code style={{ color: C.gold }}>grooves_level_challenges</code> (RLS admin). Al subir de nivel se desbloquean; la recompensa se abona al completar el reto (tabla <code style={{ color: C.gold }}>grooves_level_challenge_completions</code>).
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {challenges.map(c => (
            <div key={c.id} style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
              Nv.{c.min_level}+ · <strong style={{ color: C.gold }}>{c.title}</strong> — {c.grooves_reward} grooves ·{' '}
              {c.activo ? 'activo' : 'inactivo'}
            </div>
          ))}
          {!challenges.length ? <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sin retos.</span> : null}
        </div>
      </DCard>

      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Devolución / incidencia (doble asiento ledger)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10, lineHeight: 1.5 }}>
          Crea dos movimientos con <code style={{ color: C.gold }}>reason = devolucion_incidencia</code>: crédito al comprador y débito al vendedor (misma transacción e importe).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Comprador (UUID)</div>
            <input
              value={devBuyer}
              onChange={e => setDevBuyer(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 11,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Vendedor (UUID)</div>
            <input
              value={devSeller}
              onChange={e => setDevSeller(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 11,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div style={{ flex: '1 1 200px' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>transaction_id</div>
            <input
              value={devTx}
              onChange={e => setDevTx(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 11,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ width: 100 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Grooves</div>
            <input
              value={devAmt}
              onChange={e => setDevAmt(e.target.value)}
              type="number"
              min={1}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 11,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        <input
          value={devNote}
          onChange={e => setDevNote(e.target.value)}
          placeholder="Nota interna (opcional)"
          style={{
            width: '100%',
            padding: '8px 10px',
            borderRadius: 8,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 11,
            boxSizing: 'border-box',
            marginBottom: 10,
          }}
        />
        <BtnGold type="button" style={{ width: 'auto', minWidth: 200 }} disabled={devBusy} onClick={submitDevolucionGrooves}>
          {devBusy ? 'Aplicando…' : 'Registrar devolución (ledger x2)'}
        </BtnGold>
      </DCard>

      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
          Ledger. Filtra por <code style={{ color: C.gold }}>profile_id</code> (UUID).
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="profile_id (opcional)"
            style={{
              flex: '1 1 280px',
              minWidth: 220,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              fontSize: 13,
            }}
          />
          <BtnGold type="button" style={{ width: 'auto', minWidth: 120 }} onClick={load} disabled={loading}>
            {loading ? 'Cargando…' : 'Buscar'}
          </BtnGold>
        </div>
      </DCard>

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
      ) : (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 980 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px' }}>Fecha</th>
                  <th style={{ padding: '10px 12px' }}>Profile</th>
                  <th style={{ padding: '10px 12px' }}>Delta</th>
                  <th style={{ padding: '10px 12px' }}>Reason</th>
                  <th style={{ padding: '10px 12px' }}>Ref</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
                      {String(r.profile_id).slice(0, 8)}…
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 900, color: r.delta >= 0 ? '#9fe8ca' : C.danger }}>
                      {r.delta}
                    </td>
                    <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.reason}</td>
                    <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                      {r.ref_type ? `${r.ref_type}${r.ref_id ? ` · ${String(r.ref_id).slice(0, 8)}…` : ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin movimientos.</div> : null}
          </div>
        </DCard>
      )}
    </div>
  );
}

function AdminEstadisticasPanel({ onError }) {
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [newUsersByDay, setNewUsersByDay] = useState([]);
  const [newListingsByDay, setNewListingsByDay] = useState([]);
  const [activityByDay, setActivityByDay] = useState([]);
  const [kpi, setKpi] = useState({
    listingsActive: 0,
    txCompleted30d: 0,
    newUsers7d: 0,
    newListings7d: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const now = Date.now();
      const d30 = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
      const d7 = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [an, profs, lists, txs, act] = await Promise.all([
        supabase.from('user_site_analytics').select('zone, created_at').order('created_at', { ascending: false }).limit(2000),
        supabase.from('profiles').select('id, created_at').gte('created_at', d30).order('created_at', { ascending: false }).limit(4000),
        supabase.from('listings').select('id, created_at').gte('created_at', d30).order('created_at', { ascending: false }).limit(4000),
        supabase
          .from('transactions')
          .select('id, created_at, buyer_confirmed_at, status')
          .gte('created_at', d30)
          .order('created_at', { ascending: false })
          .limit(4000),
        supabase.from('user_site_analytics').select('created_at').gte('created_at', d7).order('created_at', { ascending: false }).limit(8000),
      ]);
      if (an.error) throw an.error;
      if (profs.error) throw profs.error;
      if (lists.error) throw lists.error;
      if (txs.error) throw txs.error;
      if (act.error) throw act.error;

      const map = {};
      for (const r of an.data ?? []) {
        const z = String(r.zone || 'unknown');
        map[z] = (map[z] ?? 0) + 1;
      }
      setZones(
        Object.entries(map)
          .map(([zone, count]) => ({ zone, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 18)
      );

      const dayKey = d =>
        d
          ? new Date(d).toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : '';
      const bucket = rows => {
        const m = {};
        for (const r of rows ?? []) {
          const k = dayKey(r.created_at);
          if (!k) continue;
          m[k] = (m[k] ?? 0) + 1;
        }
        return Object.entries(m)
          .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
          .slice(0, 14)
          .map(([day, count]) => ({ day, count }));
      };
      setNewUsersByDay(bucket(profs.data));
      setNewListingsByDay(bucket(lists.data));

      const actMap = {};
      for (const r of act.data ?? []) {
        const k = dayKey(r.created_at);
        if (!k) continue;
        actMap[k] = (actMap[k] ?? 0) + 1;
      }
      setActivityByDay(
        Object.entries(actMap)
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
          .map(([day, count]) => ({ day, count }))
      );

      const [{ count: la }, txList] = await Promise.all([
        supabase.from('listings').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        Promise.resolve(txs.data ?? []),
      ]);
      const txDone = txList.filter(
        t => t.buyer_confirmed_at != null || String(t.status || '').toLowerCase() === 'completed'
      ).length;
      const u7 = (profs.data ?? []).filter(r => r.created_at && new Date(r.created_at) >= new Date(d7)).length;
      const l7 = (lists.data ?? []).filter(r => r.created_at && new Date(r.created_at) >= new Date(d7)).length;
      setKpi({
        listingsActive: la ?? 0,
        txCompleted30d: txDone,
        newUsers7d: u7,
        newListings7d: l7,
      });
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar estadísticas');
      setZones([]);
      setNewUsersByDay([]);
      setNewListingsByDay([]);
      setActivityByDay([]);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const maxAct = useMemo(() => Math.max(1, ...activityByDay.map(d => d.count)), [activityByDay]);

  return (
    <div style={{ maxWidth: 900 }}>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 10 }}>
          Métricas operativas (últimos 30 días salvo indicación). Sin librerías externas.
        </div>
        <BtnGold type="button" style={{ width: 'auto', minWidth: 120 }} onClick={load} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </BtnGold>
      </DCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { k: 'Listings activos', v: kpi.listingsActive },
          { k: 'Ventas completadas (30d)', v: kpi.txCompleted30d },
          { k: 'Usuarios nuevos (7d)', v: kpi.newUsers7d },
          { k: 'Listings nuevos (7d)', v: kpi.newListings7d },
        ].map(x => (
          <DCard key={x.k} style={{ padding: 12 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{x.k}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: C.gold, marginTop: 6 }}>{loading ? '—' : x.v}</div>
          </DCard>
        ))}
      </div>

      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Actividad en app (eventos analytics, últimos 7 días)</div>
        {loading ? (
          <div style={{ color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
        ) : !activityByDay.length ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin trazas en la ventana.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, paddingTop: 8 }}>
            {activityByDay.map(d => (
              <div key={d.day} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div
                  title={`${d.day}: ${d.count}`}
                  style={{
                    width: '100%',
                    maxWidth: 36,
                    margin: '0 auto',
                    height: `${Math.max(6, Math.round((d.count / maxAct) * 100))}%`,
                    minHeight: 8,
                    borderRadius: 6,
                    background: 'linear-gradient(180deg, rgba(200,168,75,0.85), rgba(200,168,75,0.25))',
                  }}
                />
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{d.day.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </DCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 12 }}>
        <DCard style={{ padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Usuarios nuevos por día (14d)</div>
          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.45)' }}>…</div>
          ) : (
            <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 11 }}>
              {newUsersByDay.map(r => (
                <div key={r.day} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{r.day}</span>
                  <span style={{ color: C.gold, fontWeight: 800 }}>{r.count}</span>
                </div>
              ))}
              {!newUsersByDay.length ? <span style={{ color: 'rgba(255,255,255,0.35)' }}>Sin altas.</span> : null}
            </div>
          )}
        </DCard>
        <DCard style={{ padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Listings publicados por día (14d)</div>
          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.45)' }}>…</div>
          ) : (
            <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 11 }}>
              {newListingsByDay.map(r => (
                <div key={r.day} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{r.day}</span>
                  <span style={{ color: C.gold, fontWeight: 800 }}>{r.count}</span>
                </div>
              ))}
              {!newListingsByDay.length ? <span style={{ color: 'rgba(255,255,255,0.35)' }}>Sin datos.</span> : null}
            </div>
          )}
        </DCard>
      </div>

      <DCard style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
          Top zonas desde <code style={{ color: C.gold }}>user_site_analytics</code> (muestra reciente).
        </div>
        {loading ? (
          <div style={{ color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
        ) : !zones.length ? (
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>Sin datos.</div>
        ) : (
          zones.map(z => (
            <div key={z.zone} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ color: '#fff', fontWeight: 700 }}>{z.zone}</div>
              <div style={{ color: C.gold, fontWeight: 900 }}>{z.count}</div>
            </div>
          ))
        )}
      </DCard>
    </div>
  );
}

function AdminValoracionesPanel({ user, onError, onBadgesRefresh, menuBadges }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});
  const [onlyLow, setOnlyLow] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const badgeRows = useMemo(() => rows.filter(r => r.rating <= 2 && r.is_visible), [rows]);
  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.REVIEW_LOW,
    rows: badgeRows,
    onBadgesRefresh,
    onError,
  });

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      let q = supabase
        .from('reviews')
        .select('id, transaction_id, reviewer_id, reviewed_id, role, rating, tags, comment, is_visible, created_at')
        .order('created_at', { ascending: false })
        .limit(120);
      if (onlyLow) q = q.lte('rating', 2);
      const { data: revs, error } = await q;
      if (error) throw error;
      const list = revs ?? [];
      const ids = [...new Set([...list.map(r => r.reviewer_id), ...list.map(r => r.reviewed_id)].filter(Boolean))];
      let map = {};
      if (ids.length) {
        const { data: profs, error: pe } = await supabase.from('profiles').select('id, username').in('id', ids);
        if (pe) throw pe;
        map = Object.fromEntries((profs ?? []).map(p => [p.id, p.username]));
      }
      setNames(map);
      setRows(list);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar valoraciones');
      setRows([]);
      setNames({});
    } finally {
      setLoading(false);
    }
  }, [onError, onlyLow]);

  useEffect(() => {
    load();
  }, [load]);

  const setVisible = async (reviewId, isVisible) => {
    setBusyId(reviewId);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'review_set_visible', payload: { review_id: reviewId, is_visible: isVisible } },
      });
      if (error || !data?.ok) throw error || new Error('No se pudo actualizar');
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: isVisible ? 'review_visible_ui' : 'review_hidden_ui',
        entity_type: 'review',
        entity_id: reviewId,
        summary: isVisible ? 'Valoración mostrada' : 'Valoración oculta',
        metadata: {},
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo cambiar visibilidad');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyLow} onChange={e => setOnlyLow(e.target.checked)} />
            Solo rating 1–2 (moderación rápida)
          </label>
          <AdminSeguimientoMarcarTodas
            unreadCount={adminMenuBadgeCount(menuBadges, 'valoraciones')}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
          <button type="button" onClick={load} style={{ ...btnMini, marginLeft: 'auto' }} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
        </div>
      </DCard>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1100 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                <th style={{ padding: '10px 12px' }}>Fecha</th>
                <th style={{ padding: '10px 12px' }}>Rating</th>
                <th style={{ padding: '10px 12px' }}>Quién valora</th>
                <th style={{ padding: '10px 12px' }}>Valorado</th>
                <th style={{ padding: '10px 12px' }}>Role</th>
                <th style={{ padding: '10px 12px' }}>Comentario</th>
                <th style={{ padding: '10px 12px' }}>Visible</th>
                <th style={{ padding: '10px 12px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const track = r.rating <= 2 && r.is_visible;
                const sinLeer = track && seguimiento.isUnread(r.id);
                return (
                <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                    {track ? (
                      <AdminLeidaCell
                        leida={seguimiento.readIds.has(r.id)}
                        busy={seguimiento.readBusyId === r.id}
                        onChange={checked => seguimiento.setReadState(r.id, checked)}
                      />
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 900, color: r.rating <= 2 ? C.danger : C.gold }}>{r.rating}</td>
                  <td style={{ padding: '10px 12px', color: C.gold }}>@{names[r.reviewer_id] || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.8)' }}>@{names[r.reviewed_id] || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{r.role}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 360, color: 'rgba(255,255,255,0.78)' }}>{r.comment?.trim() || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>{r.is_visible ? 'Sí' : 'No'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      type="button"
                      style={btnMini}
                      disabled={busyId === r.id}
                      onClick={() => setVisible(r.id, !r.is_visible)}
                    >
                      {r.is_visible ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin valoraciones.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminReportesPanel({ user, onError, onBadgesRefresh, menuBadges }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [listingRows, setListingRows] = useState([]);
  const [msgRows, setMsgRows] = useState([]);
  const [errRows, setErrRows] = useState([]);
  const [errTipoFilt, setErrTipoFilt] = useState('');
  const [errResueltoFilt, setErrResueltoFilt] = useState('all'); // all | pending | done
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState('usuarios'); // usuarios | anuncios | mensajes | soporte | errores
  const [convModal, setConvModal] = useState(null);

  const errSupportRows = useMemo(
    () => (errRows || []).filter(r => r?.tipo === 'user_report' || r?.tipo === 'user_suggestion'),
    [errRows],
  );

  const errTechRows = useMemo(
    () => (errRows || []).filter(r => !(r?.tipo === 'user_report' || r?.tipo === 'user_suggestion')),
    [errRows],
  );

  const errActiveBaseRows = tab === 'soporte' ? errSupportRows : errTechRows;

  const errFiltered = useMemo(() => {
    return (errActiveBaseRows || []).filter(r => {
      if (errTipoFilt && r.tipo !== errTipoFilt) return false;
      if (errResueltoFilt === 'pending' && r.resuelto) return false;
      if (errResueltoFilt === 'done' && !r.resuelto) return false;
      return true;
    });
  }, [errActiveBaseRows, errTipoFilt, errResueltoFilt]);

  const errOpenCount = useMemo(() => (errTechRows || []).filter(r => !r.resuelto).length, [errTechRows]);
  const soporteOpenCount = useMemo(() => (errSupportRows || []).filter(r => !r.resuelto).length, [errSupportRows]);

  const reportesTabBadges = useMemo(() => adminReportesTabBadges(menuBadges), [menuBadges]);

  const seguimientoSection =
    tab === 'usuarios'
      ? ADMIN_SEG.USER_REPORT
      : tab === 'anuncios'
        ? ADMIN_SEG.LISTING_REPORT
        : tab === 'mensajes'
          ? ADMIN_SEG.MESSAGE_REPORT
          : tab === 'errores' || tab === 'soporte'
            ? ADMIN_SEG.APP_ERROR
            : null;

  const seguimientoRows = useMemo(() => {
    if (tab === 'usuarios') return rows.filter(r => r.status === 'open');
    if (tab === 'anuncios') return listingRows.filter(r => r.status === 'open');
    if (tab === 'mensajes') return msgRows;
    if (tab === 'soporte') return errSupportRows.filter(r => !r.resuelto);
    if (tab === 'errores') return errTechRows.filter(r => !r.resuelto);
    return [];
  }, [tab, rows, listingRows, msgRows, errSupportRows, errTechRows]);

  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: seguimientoSection || ADMIN_SEG.USER_REPORT,
    rows: seguimientoRows,
    onBadgesRefresh,
    onError,
  });

  const reportTabBadge = tid => {
    if (tid === 'usuarios') return reportesTabBadges.usuarios;
    if (tid === 'anuncios') return reportesTabBadges.anuncios;
    if (tid === 'mensajes') return reportesTabBadges.mensajes;
    if (tid === 'errores') return reportesTabBadges.errores;
    if (tid === 'soporte') return 0;
    return 0;
  };

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const [u, l, m, appE] = await Promise.all([
        supabase
          .from('user_reports')
          .select('id, reporter_id, target_user_id, category, body, status, reviewed_by, reviewed_at, created_at')
          .order('created_at', { ascending: false })
          .limit(90),
        supabase
          .from('listing_reports')
          .select(
            `
            id,
            listing_id,
            reporter_id,
            category,
            body,
            status,
            reviewed_by,
            reviewed_at,
            created_at,
            listings:listing_id ( id, title, artist, price, status, seller_id, profiles:seller_id ( username ) )
          `
          )
          .order('created_at', { ascending: false })
          .limit(90),
        supabase
          .from('message_reports')
          .select(
            `
            id,
            message_id,
            conversation_id,
            reporter_id,
            category,
            body,
            status,
            reviewed_by,
            reviewed_at,
            created_at,
            messages:message_id ( id, sender_id, content, created_at, moderation_status )
          `
          )
          .eq('status', 'open')
          .order('created_at', { ascending: false })
          .limit(90),
        supabase
          .from('app_errors')
          .select(
            'id, created_at, tipo, mensaje, stack, pagina, metadata, resuelto, user_id, profiles:user_id ( username )',
          )
          .order('created_at', { ascending: false })
          .limit(200),
      ]);
      if (u.error) throw u.error;
      if (l.error) throw l.error;
      if (m.error) throw m.error;
      if (appE.error) throw appE.error;
      setRows(u.data ?? []);
      setListingRows(l.data ?? []);
      setMsgRows(m.data ?? []);
      setErrRows(appE.data ?? []);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar reportes');
      setRows([]);
      setListingRows([]);
      setMsgRows([]);
      setErrRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, next) => {
    setBusy(id);
    onError('');
    try {
      const patch = { status: next, reviewed_at: new Date().toISOString(), reviewed_by: user?.id ?? null };
      const { error } = await supabase.from('user_reports').update(patch).eq('id', id);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: next === 'dismissed' ? 'user_report_dismissed' : 'user_report_reviewed',
        entity_type: 'user_report',
        entity_id: id,
        summary: `Reporte usuario ${next === 'dismissed' ? 'descartado' : 'revisado'}`,
        metadata: { status: next },
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar');
    } finally {
      setBusy(null);
    }
  };

  const setMsgStatus = async (id, next) => {
    setBusy(id);
    onError('');
    try {
      const patch = { status: next, reviewed_at: new Date().toISOString(), reviewed_by: user?.id ?? null };
      const { error } = await supabase.from('message_reports').update(patch).eq('id', id);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: next === 'dismissed' ? 'message_report_dismissed' : 'message_report_reviewed',
        entity_type: 'message_report',
        entity_id: id,
        summary: `Reporte de mensaje ${next === 'dismissed' ? 'descartado' : 'revisado'}`,
        metadata: { status: next },
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar');
    } finally {
      setBusy(null);
    }
  };

  const setListingStatus = async (id, next) => {
    setBusy(id);
    onError('');
    try {
      const patch = { status: next, reviewed_at: new Date().toISOString(), reviewed_by: user?.id ?? null };
      const { error } = await supabase.from('listing_reports').update(patch).eq('id', id);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: next === 'dismissed' ? 'listing_report_dismissed' : 'listing_report_reviewed',
        entity_type: 'listing_report',
        entity_id: id,
        summary: `Reporte anuncio ${next === 'dismissed' ? 'descartado' : 'revisado'}`,
        metadata: { status: next },
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar');
    } finally {
      setBusy(null);
    }
  };

  const openConversationView = async conversationId => {
    if (!conversationId) return;
    setConvModal({ conversationId, loading: true, messages: [] });
    onError('');
    try {
      const { data, error } = await supabase.rpc('admin_fetch_conversation_messages', {
        p_conversation_id: conversationId,
      });
      if (error) throw error;
      setConvModal({ conversationId, loading: false, messages: data ?? [] });
    } catch (e) {
      onError(e?.message || 'No se pudo cargar el hilo');
      setConvModal(null);
    }
  };

  const hideMessage = async (messageId, reportId) => {
    if (!messageId) return;
    setBusy(reportId || messageId);
    onError('');
    try {
      const { error } = await supabase.from('messages').update({ moderation_status: 'hidden' }).eq('id', messageId);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'message_hidden',
        entity_type: 'message',
        entity_id: messageId,
        summary: 'Mensaje ocultado por moderación',
        metadata: {},
      });
      await load();
      onBadgesRefresh?.();
      if (convModal?.conversationId) {
        await openConversationView(convModal.conversationId);
      }
    } catch (e) {
      onError(e?.message || 'No se pudo ocultar el mensaje');
    } finally {
      setBusy(null);
    }
  };

  const resolveAppError = async id => {
    setBusy(id);
    onError('');
    try {
      const { error } = await supabase.from('app_errors').update({ resuelto: true }).eq('id', id);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'app_error_resolved',
        entity_type: 'app_error',
        entity_id: id,
        summary: 'Error técnico marcado como resuelto',
        metadata: {},
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo marcar como resuelto');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {[
          { id: 'usuarios', label: `Usuarios (${rows.filter(r => r.status === 'open').length})` },
          { id: 'anuncios', label: `Anuncios (${listingRows.filter(r => r.status === 'open').length})` },
          { id: 'mensajes', label: `Mensajes (${msgRows.length})` },
          { id: 'soporte', label: `Soporte web (${soporteOpenCount})` },
          { id: 'errores', label: `Errores técnicos (${errOpenCount})` },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: `0.5px solid ${tab === t.id ? C.gold : 'rgba(255,255,255,0.12)'}`,
              background: tab === t.id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
              color: tab === t.id ? C.gold : 'rgba(255,255,255,0.65)',
              fontSize: 12,
              fontWeight: tab === t.id ? 700 : 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <AdminSeguimientoTabLabel label={t.label} unreadCount={reportTabBadge(t.id)} />
          </button>
        ))}
        <AdminSeguimientoMarcarTodas
          unreadCount={seguimiento.unreadCount}
          markingAllRead={seguimiento.markingAllRead}
          onMarcarTodas={seguimiento.marcarTodasLeidas}
        />
        <button type="button" onClick={load} style={{ ...btnMini, marginLeft: 'auto' }} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </div>

      {tab === 'usuarios' ? (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1100 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                  <th style={{ padding: '10px 12px' }}>Fecha</th>
                  <th style={{ padding: '10px 12px' }}>Categoría</th>
                  <th style={{ padding: '10px 12px' }}>Reporte</th>
                  <th style={{ padding: '10px 12px' }}>Estado</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const track = r.status === 'open';
                  const sinLeer = track && seguimiento.isUnread(r.id);
                  return (
                  <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      {track ? (
                        <AdminLeidaCell
                          leida={seguimiento.readIds.has(r.id)}
                          busy={seguimiento.readBusyId === r.id}
                          onChange={checked => seguimiento.setReadState(r.id, checked)}
                        />
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.category}</td>
                    <td style={{ padding: '10px 12px', maxWidth: 520, color: 'rgba(255,255,255,0.78)' }}>{r.body?.trim() || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{r.status}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => setStatus(r.id, 'reviewed')}>
                          Revisado
                        </button>
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => setStatus(r.id, 'dismissed')}>
                          Descartar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin reportes.</div> : null}
          </div>
        </DCard>
      ) : tab === 'anuncios' ? (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1200 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                  <th style={{ padding: '10px 12px' }}>Fecha</th>
                  <th style={{ padding: '10px 12px' }}>Categoría</th>
                  <th style={{ padding: '10px 12px' }}>Anuncio</th>
                  <th style={{ padding: '10px 12px' }}>Estado</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listingRows.map(r => {
                  const track = r.status === 'open';
                  const sinLeer = track && seguimiento.isUnread(r.id);
                  return (
                  <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      {track ? (
                        <AdminLeidaCell
                          leida={seguimiento.readIds.has(r.id)}
                          busy={seguimiento.readBusyId === r.id}
                          onChange={checked => seguimiento.setReadState(r.id, checked)}
                        />
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.category}</td>
                    <td style={{ padding: '10px 12px', maxWidth: 620, color: 'rgba(255,255,255,0.78)' }}>
                      <div style={{ fontWeight: 900, marginBottom: 4 }}>
                        {r.listings?.title || '—'} {r.listings?.artist ? `· ${r.listings.artist}` : ''}
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                        {r.listings?.profiles?.username ? `@${r.listings.profiles.username}` : '—'}{' '}
                        {r.listings?.price != null ? `· ${r.listings.price}€` : ''}{' '}
                        {r.listings?.status ? `· ${r.listings.status}` : ''}
                      </div>
                      {r.body?.trim() ? (
                        <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Reporte: {r.body}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{r.status}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => setListingStatus(r.id, 'reviewed')}>
                          Revisado
                        </button>
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => setListingStatus(r.id, 'dismissed')}>
                          Descartar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            {!listingRows.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin reportes de anuncios.</div>
            ) : null}
          </div>
        </DCard>
      ) : tab === 'mensajes' ? (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1200 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                  <th style={{ padding: '10px 12px' }}>Fecha</th>
                  <th style={{ padding: '10px 12px' }}>Categoría</th>
                  <th style={{ padding: '10px 12px' }}>Mensaje</th>
                  <th style={{ padding: '10px 12px' }}>Estado msg</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {msgRows.map(r => {
                  const track = true;
                  const sinLeer = track && seguimiento.isUnread(r.id);
                  return (
                  <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <AdminLeidaCell
                        leida={seguimiento.readIds.has(r.id)}
                        busy={seguimiento.readBusyId === r.id}
                        onChange={checked => seguimiento.setReadState(r.id, checked)}
                      />
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.category}</td>
                    <td style={{ padding: '10px 12px', maxWidth: 620, color: 'rgba(255,255,255,0.78)' }}>
                      <div style={{ fontWeight: 800, fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                        {r.messages?.sender_id ? String(r.messages.sender_id).slice(0, 8) + '…' : ''}{' '}
                        {r.conversation_id ? `· conv ${String(r.conversation_id).slice(0, 8)}…` : ''}
                      </div>
                      {r.messages?.content?.trim() || '—'}
                      {r.body?.trim() ? (
                        <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Reporte: {r.body}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{r.messages?.moderation_status || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {r.conversation_id ? (
                          <button
                            type="button"
                            style={btnMini}
                            disabled={busy === r.id}
                            onClick={() => openConversationView(r.conversation_id)}
                          >
                            Ver hilo
                          </button>
                        ) : null}
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => setMsgStatus(r.id, 'reviewed')}>
                          Revisado
                        </button>
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => setMsgStatus(r.id, 'dismissed')}>
                          Descartar
                        </button>
                        <button
                          type="button"
                          style={{ ...btnMini, borderColor: 'rgba(239,159,39,0.55)', color: 'rgba(239,159,39,0.95)' }}
                          disabled={busy === r.id}
                          onClick={() => hideMessage(r.message_id, r.id)}
                        >
                          Ocultar msg
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            {!msgRows.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin reportes de mensajes abiertos.</div>
            ) : null}
          </div>
        </DCard>
      ) : (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
              Tipo
              <select
                value={errTipoFilt}
                onChange={e => setErrTipoFilt(e.target.value)}
                style={{
                  minWidth: 160,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontSize: 12,
                }}
              >
                <option value="">Todos</option>
                {tab === 'soporte' ? (
                  <>
                    <option value="user_report">user_report</option>
                    <option value="user_suggestion">user_suggestion</option>
                  </>
                ) : (
                  <>
                    <option value="js_error">js_error</option>
                    <option value="unhandled_rejection">unhandled_rejection</option>
                    <option value="edge_function">edge_function</option>
                    <option value="grooves_inconsistency">grooves_inconsistency</option>
                  </>
                )}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
              Estado
              <select
                value={errResueltoFilt}
                onChange={e => setErrResueltoFilt(e.target.value)}
                style={{
                  minWidth: 140,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontSize: 12,
                }}
              >
                <option value="all">Todos</option>
                <option value="pending">Pendientes</option>
                <option value="done">Resueltos</option>
              </select>
            </label>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 'auto' }}>
              Mostrando {errFiltered.length} de {errActiveBaseRows.length}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1180 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                  <th style={{ padding: '10px 12px' }}>Fecha</th>
                  <th style={{ padding: '10px 12px' }}>Origen</th>
                  <th style={{ padding: '10px 12px' }}>Tipo</th>
                  <th style={{ padding: '10px 12px', maxWidth: 200 }}>Página</th>
                  <th style={{ padding: '10px 12px' }}>Mensaje</th>
                  <th style={{ padding: '10px 12px' }}>Usuario</th>
                  <th style={{ padding: '10px 12px' }}>Resuelto</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {errFiltered.map(r => {
                  const track = !r.resuelto;
                  const sinLeer = track && seguimiento.isUnread(r.id);
                  const msgShort =
                    r.mensaje && r.mensaje.length > 160 ? `${String(r.mensaje).slice(0, 160)}…` : r.mensaje || '—';
                  const pageShort =
                    r.pagina && r.pagina.length > 72 ? `${String(r.pagina).slice(0, 72)}…` : r.pagina || '—';
                  const who = r.profiles?.username
                    ? `@${r.profiles.username}`
                    : r.user_id
                      ? `${String(r.user_id).slice(0, 8)}…`
                      : '—';
                  const isManual = r.tipo === 'user_report' || r.tipo === 'user_suggestion';
                  const origenLabel = isManual ? 'Manual' : 'Auto';
                  return (
                    <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        {track ? (
                          <AdminLeidaCell
                            leida={seguimiento.readIds.has(r.id)}
                            busy={seguimiento.readBusyId === r.id}
                            onChange={checked => seguimiento.setReadState(r.id, checked)}
                          />
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 10, fontWeight: 900, color: isManual ? 'rgba(200,168,75,0.92)' : 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>
                        {origenLabel}
                      </td>
                      <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800, whiteSpace: 'nowrap' }}>{r.tipo}</td>
                      <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.65)', maxWidth: 200 }} title={r.pagina || ''}>
                        {pageShort}
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: 420, color: 'rgba(255,255,255,0.82)', lineHeight: 1.35 }} title={r.mensaje || ''}>
                        {msgShort}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>{who}</td>
                      <td style={{ padding: '10px 12px', color: r.resuelto ? C.green : C.warn, fontWeight: 800 }}>{r.resuelto ? 'Sí' : 'No'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {!r.resuelto ? (
                          <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => resolveAppError(r.id)}>
                            Marcar resuelto
                          </button>
                        ) : (
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!errFiltered.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin entradas con estos filtros.</div>
            ) : null}
          </div>
        </DCard>
      )}

      {convModal ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 12000,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={() => setConvModal(null)}
        >
          <DCard
            style={{ width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '14px 16px', borderBottom: '0.5px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Hilo de conversación</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                  {String(convModal.conversationId).slice(0, 8)}… · solo lectura (moderación)
                </div>
              </div>
              <button
                type="button"
                onClick={() => setConvModal(null)}
                style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 18, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {convModal.loading ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Cargando mensajes…</div>
              ) : !convModal.messages?.length ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Sin mensajes en este hilo.</div>
              ) : (
                convModal.messages.map(m => (
                  <div
                    key={m.id}
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.05)',
                      border: '0.5px solid rgba(255,255,255,0.08)',
                      opacity: m.moderation_status === 'hidden' ? 0.55 : 1,
                    }}
                  >
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>
                      {m.created_at ? new Date(m.created_at).toLocaleString('es-ES') : '—'} ·{' '}
                      {String(m.sender_id || '').slice(0, 8)}…
                      {m.moderation_status === 'hidden' ? ' · oculto' : ''}
                    </div>
                    <div style={{ fontSize: 12, color: '#fff', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</div>
                  </div>
                ))
              )}
            </div>
          </DCard>
        </div>
      ) : null}
    </div>
  );
}

function AdminNotificacionesPanel({ onError }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [form, setForm] = useState({
    segment: 'all',
    type: 'admin_broadcast',
    title: '',
    body: '',
    pais: '',
    ciudad: '',
    localidad: '',
    zone: '',
    since_days: 30,
  });

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, actor_id, type, title, body, is_read, created_at')
        .order('created_at', { ascending: false })
        .limit(120);
      if (error) throw error;
      setRows(data ?? []);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar notificaciones');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const sendBroadcast = async () => {
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title && !body) {
      onError('Indica al menos un título o un mensaje.');
      return;
    }
    setSending(true);
    setSentInfo('');
    onError('');
    try {
      const { data, error } = await supabase.rpc('admin_send_broadcast', {
        p_segment: form.segment,
        p_type: form.type || 'admin_broadcast',
        p_title: title || null,
        p_body: body || null,
        p_data: {},
        p_filters: {
          pais: form.pais || null,
          ciudad: form.ciudad || null,
          localidad: form.localidad || null,
          zone: form.zone || null,
          since_days: form.since_days || null,
        },
      });
      if (error) throw error;
      const n = typeof data === 'number' ? data : Number(data || 0);
      await supabase.from('admin_activity_log').insert({
        action: 'notifications_broadcast_sent',
        entity_type: 'notifications',
        entity_id: null,
        summary: `Comunicado enviado (${form.segment}) · ${n} usuarios`,
        metadata: { segment: form.segment, count: n, type: form.type, title: title || null },
      });
      setSentInfo(`Enviado a ${n} usuarios (${form.segment}).`);
      setForm(f => ({ ...f, title: '', body: '' }));
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo enviar el comunicado');
    } finally {
      setSending(false);
    }
  };

  const previewRecipients = async () => {
    setPreviewing(true);
    setPreviewCount(null);
    onError('');
    try {
      const { data, error } = await supabase.rpc('admin_count_broadcast_recipients', {
        p_segment: form.segment,
        p_filters: {
          pais: form.pais || null,
          ciudad: form.ciudad || null,
          localidad: form.localidad || null,
          zone: form.zone || null,
          since_days: form.since_days || null,
        },
      });
      if (error) throw error;
      setPreviewCount(typeof data === 'number' ? data : Number(data || 0));
    } catch (e) {
      onError(e?.message || 'No se pudo previsualizar el segmento');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div>
      <DCard style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Enviar comunicado</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <select
            value={form.segment}
            onChange={e => setForm(f => ({ ...f, segment: e.target.value }))}
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <option value="all">Todos</option>
            <option value="active">Cuentas activas</option>
            <option value="verified">Verificados</option>
            <option value="stores">Comercio (is_store)</option>
            <option value="new_7d">Nuevos últimos 7 días</option>
            <option value="pais">País</option>
            <option value="ciudad">Ciudad</option>
            <option value="localidad">Localidad</option>
            <option value="ubicacion">Ubicación (pais/ciudad/localidad)</option>
            <option value="zone">Zona (analytics)</option>
          </select>
          <input
            value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            placeholder="type (admin_broadcast)"
            style={{
              flex: '1 1 220px',
              minWidth: 200,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              fontSize: 12,
            }}
          />
        </div>

        {['pais', 'ciudad', 'localidad', 'ubicacion'].includes(form.segment) ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <input
              value={form.pais}
              onChange={e => setForm(f => ({ ...f, pais: e.target.value }))}
              placeholder="País (p. ej. España)"
              style={{
                flex: '1 1 180px',
                minWidth: 160,
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 12,
              }}
            />
            <input
              value={form.ciudad}
              onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))}
              placeholder="Ciudad / provincia"
              style={{
                flex: '1 1 180px',
                minWidth: 160,
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 12,
              }}
            />
            <input
              value={form.localidad}
              onChange={e => setForm(f => ({ ...f, localidad: e.target.value }))}
              placeholder="Localidad / municipio"
              style={{
                flex: '1 1 180px',
                minWidth: 160,
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 12,
              }}
            />
          </div>
        ) : null}

        {form.segment === 'zone' ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <input
              value={form.zone}
              onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}
              placeholder="zone (p. ej. marketplace, noticias, comunidad)"
              style={{
                flex: '1 1 260px',
                minWidth: 220,
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 12,
              }}
            />
            <input
              value={String(form.since_days ?? 30)}
              onChange={e => setForm(f => ({ ...f, since_days: Number(e.target.value || 0) }))}
              placeholder="since_days"
              style={{
                width: 120,
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 12,
              }}
            />
          </div>
        ) : null}
        <input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Título (opcional)"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.06)',
            color: '#fff',
            fontSize: 13,
            marginBottom: 10,
            boxSizing: 'border-box',
          }}
        />
        <textarea
          value={form.body}
          onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
          placeholder="Mensaje del comunicado…"
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.06)',
            color: '#fff',
            fontSize: 13,
            marginBottom: 10,
            boxSizing: 'border-box',
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <BtnGold type="button" style={{ width: 'auto', minWidth: 170 }} onClick={sendBroadcast} disabled={sending}>
            {sending ? 'Enviando…' : 'Enviar comunicado'}
          </BtnGold>
          <button
            type="button"
            onClick={previewRecipients}
            disabled={previewing || sending}
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              border: '0.5px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 750,
              fontSize: 12,
              cursor: previewing ? 'wait' : 'pointer',
              opacity: previewing ? 0.7 : 1,
            }}
          >
            {previewing ? 'Calculando…' : 'Previsualizar destinatarios'}
          </button>
          {sentInfo ? <div style={{ fontSize: 12, color: '#9fe8ca', fontWeight: 700 }}>{sentInfo}</div> : null}
          {previewCount != null ? (
            <div style={{ fontSize: 12, color: C.gold, fontWeight: 900 }}>{previewCount} destinatarios</div>
          ) : null}
        </div>
      </DCard>

      <button type="button" onClick={load} style={{ ...btnMini, marginBottom: 12 }} disabled={loading}>
        {loading ? 'Cargando…' : 'Recargar'}
      </button>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1100 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px' }}>Fecha</th>
                <th style={{ padding: '10px 12px' }}>Tipo</th>
                <th style={{ padding: '10px 12px' }}>Título</th>
                <th style={{ padding: '10px 12px' }}>Leída</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(n => (
                <tr key={n.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                    {n.created_at ? new Date(n.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{n.type}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 720, color: 'rgba(255,255,255,0.78)' }}>
                    {n.title?.trim() || '—'}
                    {n.body?.trim() ? <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{n.body}</div> : null}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{n.is_read ? 'Sí' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin notificaciones.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminEventosPanel({ user, onError, onBadgesRefresh, menuBadges }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [filtCity, setFiltCity] = useState('');
  const [filtType, setFiltType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const finp = {
    padding: '8px 10px',
    borderRadius: 8,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 12,
  };

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      let q = supabase
        .from('events')
        .select('id, author_id, title, type, city, start_date, end_date, status, created_at, external_url, is_featured')
        .order('created_at', { ascending: false })
        .limit(200);
      const fc = filtCity.trim();
      if (fc) q = q.ilike('city', `%${fc}%`);
      const ft = filtType.trim();
      if (ft) q = q.eq('type', ft);
      if (dateFrom.trim()) q = q.gte('start_date', dateFrom.trim());
      if (dateTo.trim()) q = q.lte('start_date', dateTo.trim());
      const { data, error } = await q;
      if (error) throw error;
      setRows(data ?? []);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar eventos');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError, filtCity, filtType, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (row, next) => {
    if (!row?.id || busyId) return;
    setBusyId(row.id);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'event_set_status', payload: { event_id: row.id, status: next, title: row.title ?? null } },
      });
      if (error || !data?.ok) throw error || new Error('No se pudo actualizar el estado');
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar el estado');
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeatured = async row => {
    if (!row?.id || busyId) return;
    setBusyId(`feat-${row.id}`);
    onError('');
    try {
      const next = !row.is_featured;
      const { error } = await supabase.from('events').update({ is_featured: next }).eq('id', row.id);
      if (error) throw error;
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo cambiar destacado (¿migración 20260512150000 aplicada?)');
    } finally {
      setBusyId(null);
    }
  };

  const list = rows.filter(r => {
    const st = String(r.status || 'pending');
    if (tab === 'pending') return st === 'pending';
    if (tab === 'approved') return st === 'approved';
    if (tab === 'rejected') return st === 'rejected';
    return true;
  });

  const showSeguimiento = tab === 'pending';
  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.EVENTO,
    rows: showSeguimiento ? list : [],
    onBadgesRefresh,
    onError,
  });

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Cola de la tabla <code style={{ color: C.gold }}>events</code> (eventos/ocio clásicos). El feed de publicaciones de Comunidad se modera en <strong>Contenido → Comunidad</strong>.
          Filtros por ciudad (parcial), tipo exacto y rango sobre <code style={{ color: C.gold }}>start_date</code>.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Ciudad
            <input value={filtCity} onChange={e => setFiltCity(e.target.value)} placeholder="Madrid…" style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Tipo (exacto)
            <input value={filtType} onChange={e => setFiltType(e.target.value)} placeholder="concierto…" style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Desde (start_date)
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Hasta (start_date)
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={finp} />
          </label>
          <button type="button" onClick={() => load()} style={btnMini} disabled={loading}>
            Aplicar filtros
          </button>
        </div>
      </DCard>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {[
          { id: 'pending', label: 'Pendientes' },
          { id: 'approved', label: 'Aprobados' },
          { id: 'rejected', label: 'Rechazados' },
          { id: 'all', label: 'Todos' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: `0.5px solid ${tab === t.id ? C.gold : 'rgba(255,255,255,0.12)'}`,
              background: tab === t.id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
              color: tab === t.id ? C.gold : 'rgba(255,255,255,0.65)',
              fontSize: 12,
              fontWeight: tab === t.id ? 700 : 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {t.id === 'pending' ? (
              <AdminSeguimientoTabLabel label={t.label} unreadCount={adminMenuBadgeCount(menuBadges, 'eventos')} />
            ) : (
              t.label
            )}
          </button>
        ))}
        {showSeguimiento ? (
          <AdminSeguimientoMarcarTodas
            unreadCount={seguimiento.unreadCount}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
        ) : null}
        <button type="button" onClick={load} style={{ ...btnMini, marginLeft: 'auto' }} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </div>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1180 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {showSeguimiento ? <th style={{ padding: '10px 12px', width: 72 }}>Leída</th> : null}
                <th style={{ padding: '10px 12px' }}>Título</th>
                <th style={{ padding: '10px 12px' }}>Tipo</th>
                <th style={{ padding: '10px 12px' }}>Ciudad</th>
                <th style={{ padding: '10px 12px' }}>Fechas</th>
                <th style={{ padding: '10px 12px' }}>Estado</th>
                <th style={{ padding: '10px 12px' }}>Dest.</th>
                <th style={{ padding: '10px 12px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map(e => {
                const track = showSeguimiento;
                const sinLeer = track && seguimiento.isUnread(e.id);
                return (
                <tr key={e.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                  {showSeguimiento ? (
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <AdminLeidaCell
                        leida={seguimiento.readIds.has(e.id)}
                        busy={seguimiento.readBusyId === e.id}
                        onChange={checked => seguimiento.setReadState(e.id, checked)}
                      />
                    </td>
                  ) : null}
                  <td style={{ padding: '10px 12px', fontWeight: 800, maxWidth: 260 }}>{e.title}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.7)' }}>{e.type || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{e.city || '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>
                    {e.start_date || '—'}{e.end_date ? ` → ${e.end_date}` : ''}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{e.status}</td>
                  <td style={{ padding: '10px 12px' }}>{e.is_featured ? 'Sí' : '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {String(e.status || 'pending') !== 'approved' ? (
                        <button
                          type="button"
                          style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                          disabled={busyId === e.id}
                          onClick={() => setStatus(e, 'approved')}
                        >
                          Aprobar
                        </button>
                      ) : null}
                      {String(e.status || 'pending') !== 'rejected' ? (
                        <button
                          type="button"
                          style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                          disabled={busyId === e.id}
                          onClick={() => setStatus(e, 'rejected')}
                        >
                          Rechazar
                        </button>
                      ) : null}
                      <button
                        type="button"
                        style={{ ...btnMini, borderColor: 'rgba(200,168,75,0.45)', color: C.gold }}
                        disabled={busyId === `feat-${e.id}` || busyId === e.id}
                        onClick={() => toggleFeatured(e)}
                      >
                        {e.is_featured ? 'Quitar destacado' : 'Destacar'}
                      </button>
                      {e.external_url ? (
                        <a
                          href={e.external_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            ...btnMini,
                            display: 'inline-flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            borderColor: 'rgba(255,255,255,0.18)',
                            color: 'rgba(255,255,255,0.75)',
                          }}
                        >
                          Fuente
                        </a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
          {!list.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin eventos.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminPartnerRegistroPanel({ user, onError, onBadgesRefresh, menuBadges, onGoPartners }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.PARTNER,
    rows,
    onBadgesRefresh,
    onError,
  });

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const { data, error } = await supabase
        .from('partners')
        .select(
          'id, code, company_name, nif, contact_email, contact_phone, sector, verification_status, profile_id, created_at'
        )
        .in('verification_status', ['pending', 'reviewing'])
        .order('created_at', { ascending: false })
        .limit(80);
      if (error) throw error;
      setRows(data ?? []);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar solicitudes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Cola de solicitudes desde el registro público de partner (estado <strong>pendiente</strong> o <strong>en revisión</strong>). La
          aprobación, rechazo y notas se gestionan en <strong>Partners</strong>.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={load} style={{ ...btnMini }} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
          <AdminSeguimientoMarcarTodas
            unreadCount={adminMenuBadgeCount(menuBadges, 'partner_registro')}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
          <BtnGold type="button" style={{ width: 'auto', minWidth: 180 }} onClick={() => onGoPartners?.()}>
            Abrir Partners
          </BtnGold>
        </div>
      </DCard>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1000 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                <th style={{ padding: '10px 12px' }}>Alta</th>
                <th style={{ padding: '10px 12px' }}>Estado</th>
                <th style={{ padding: '10px 12px' }}>Código</th>
                <th style={{ padding: '10px 12px' }}>Negocio</th>
                <th style={{ padding: '10px 12px' }}>Sector</th>
                <th style={{ padding: '10px 12px' }}>Email</th>
                <th style={{ padding: '10px 12px' }}>Teléfono</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const sinLeer = seguimiento.isUnread(p.id);
                return (
                <tr key={p.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                    <AdminLeidaCell
                      leida={seguimiento.readIds.has(p.id)}
                      busy={seguimiento.readBusyId === p.id}
                      onChange={checked => seguimiento.setReadState(p.id, checked)}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                    {p.created_at ? new Date(p.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{p.verification_status}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 900, color: C.gold }}>{p.code}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800 }}>{p.company_name}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{SECTOR_LABELS[p.sector] || p.sector || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{p.contact_email || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{p.contact_phone || '—'}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin solicitudes abiertas.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminSubastasPanel({ onError, onBadgesRefresh, onViewListingFicha, fichaLoadingId }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rows, setRows] = useState([]);
  const [socialNetById, setSocialNetById] = useState({});
  const [socialKitOpen, setSocialKitOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const { data: listings, error: le } = await supabase
        .from('listings')
        .select(
          'id, title, artist, seller_id, auction_start, auction_buy_now, auction_ends_at, auction_store_id, status, price, profiles:seller_id ( username ), stores:auction_store_id ( store_name, verification_status )'
        )
        .eq('is_auction', true)
        .order('created_at', { ascending: false })
        .limit(80);
      if (le) throw le;
      const list = listings ?? [];
      const ids = list.map(l => l.id).filter(Boolean);
      let bidRows = [];
      if (ids.length) {
        const { data: bids, error: be } = await supabase
          .from('bids')
          .select('listing_id, amount, deposit_paid, bidder_id, created_at')
          .in('listing_id', ids)
          .order('created_at', { ascending: false });
        if (be) throw be;
        bidRows = bids ?? [];
      }
      const statsByListing = {};
      for (const b of bidRows) {
        const lid = b.listing_id;
        if (!lid) continue;
        if (!statsByListing[lid]) {
          statsByListing[lid] = { count: 0, max: 0, deposits: 0, bidders: new Set() };
        }
        const s = statsByListing[lid];
        s.count += 1;
        s.max = Math.max(s.max, Number(b.amount) || 0);
        if (b.deposit_paid) s.deposits += 1;
        if (b.bidder_id) s.bidders.add(b.bidder_id);
      }
      const mapped = list.map(l => ({
        ...l,
        is_auction: true,
        bidStats: statsByListing[l.id] || { count: 0, max: 0, deposits: 0, bidders: new Set() },
      }));
      setRows(mapped);
      const map = await fetchSocialNetworksByContentIds(mapped.map(l => l.id));
      setSocialNetById(map);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar subastas');
      setRows([]);
      setSocialNetById({});
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const runListingStatus = async (listingId, status) => {
    if (!listingId) return;
    setBusy(listingId);
    onError('');
    try {
      await adminSetListingStatus(listingId, status);
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar la subasta');
    } finally {
      setBusy(null);
    }
  };

  const pauseAuction = id => runListingStatus(id, 'paused');
  const removeAuction = async id => {
    if (!id) return;
    if (!window.confirm('¿Retirar esta subasta del marketplace?')) return;
    await runListingStatus(id, 'removed');
  };
  const activateAuction = id => runListingStatus(id, 'active');

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Listings con <code style={{ color: C.gold }}>is_auction</code>, pujas y depósitos desde <code style={{ color: C.gold }}>bids</code>.
          Partner verificador asociado (si existe) y su estado de verificación.
        </div>
        <button type="button" onClick={load} style={{ ...btnMini }} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </DCard>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1180 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px' }}>Anuncio</th>
                <th style={{ padding: '10px 12px' }}>Vendedor</th>
                <th style={{ padding: '10px 12px' }}>Estado</th>
                <th style={{ padding: '10px 12px' }}>Fin subasta</th>
                <th style={{ padding: '10px 12px' }}>Pujas</th>
                <th style={{ padding: '10px 12px' }}>Puja máx.</th>
                <th style={{ padding: '10px 12px' }}>Depósitos</th>
                <th style={{ padding: '10px 12px' }}>Pujadores</th>
                <th style={{ padding: '10px 12px' }}>Partner envío</th>
                <th style={{ padding: '10px 12px', minWidth: 220 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const st = r.bidStats;
                const store = r.stores;
                const listingRow = {
                  id: r.id,
                  title: r.title,
                  artist: r.artist,
                  status: r.status,
                  is_auction: true,
                };
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                    <td style={{ padding: '10px 12px', maxWidth: 320 }}>
                      <div style={{ fontWeight: 800 }}>{r.title}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{r.artist}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>
                      {r.profiles?.username || String(r.seller_id || '').slice(0, 8) + '…'}
                    </td>
                    <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.status}</td>
                    <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
                      {r.auction_ends_at ? new Date(r.auction_ends_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{st.count}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 800 }}>{st.max > 0 ? `${Number(st.max).toFixed(2)} €` : '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{st.deposits}</td>
                    <td style={{ padding: '10px 12px' }}>{st.bidders?.size ?? 0}</td>
                    <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.72)' }}>
                      {store?.store_name ? (
                        <>
                          <div style={{ fontWeight: 700 }}>{store.store_name}</div>
                          <div style={{ color: C.gold }}>{store.verification_status || '—'}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <AdminListingSocialActions
                        listing={listingRow}
                        busy={busy === r.id || fichaLoadingId === r.id}
                        socialNetworks={socialNetById[r.id] || []}
                        onRedes={setSocialKitOpen}
                        onActivate={activateAuction}
                        onPause={pauseAuction}
                        onRemove={removeAuction}
                        onViewFicha={onViewListingFicha}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin subastas.</div> : null}
        </div>
      </DCard>
      <AdminSocialKitModal
        open={!!socialKitOpen}
        onClose={() => setSocialKitOpen(null)}
        contentType={socialKitOpen?.contentType ?? 'auction'}
        contentId={socialKitOpen?.contentId ?? ''}
        titleHint={socialKitOpen?.titleHint ?? ''}
        onError={onError}
      />
    </div>
  );
}

function adminTxIsCompleted(t) {
  return !!(t?.buyer_confirmed_at != null) || String(t?.status || '').toLowerCase() === 'completed';
}

function AdminTransaccionesPanel({ onError }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filterDone, setFilterDone] = useState('completed');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      let q = supabase
        .from('transactions')
        .select(
          'id, listing_id, buyer_id, seller_id, amount, commission, shipping_type, shipping_cost, status, buyer_confirmed_at, created_at'
        )
        .order('created_at', { ascending: false })
        .limit(250);
      if (fromDate) q = q.gte('created_at', `${fromDate}T00:00:00.000Z`);
      if (toDate) q = q.lte('created_at', `${toDate}T23:59:59.999Z`);
      const { data, error } = await q;
      if (error) throw error;
      let list = data ?? [];
      if (filterDone === 'completed') list = list.filter(adminTxIsCompleted);
      const lids = [...new Set(list.map(t => t.listing_id).filter(Boolean))];
      const uids = [...new Set([...list.map(t => t.buyer_id), ...list.map(t => t.seller_id)].filter(Boolean))];
      const [lr, pr] = await Promise.all([
        lids.length ? supabase.from('listings').select('id, title, artist').in('id', lids) : Promise.resolve({ data: [] }),
        uids.length ? supabase.from('profiles').select('id, username').in('id', uids) : Promise.resolve({ data: [] }),
      ]);
      const listingsMap = Object.fromEntries((lr.data ?? []).map(x => [x.id, x]));
      const profilesMap = Object.fromEntries((pr.data ?? []).map(x => [x.id, x.username]));
      setRows(
        list.map(t => ({
          ...t,
          listing: listingsMap[t.listing_id],
          buyerName: profilesMap[t.buyer_id] || '—',
          sellerName: profilesMap[t.seller_id] || '—',
        }))
      );
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar transacciones');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError, fromDate, toDate, filterDone]);

  useEffect(() => {
    load();
  }, [load]);

  const commissionByShipping = useMemo(() => {
    const map = {};
    let totalComm = 0;
    let totalAmount = 0;
    for (const t of rows) {
      const key = t.shipping_type?.trim() || '(sin tipo)';
      const c = Number(t.commission) || 0;
      const a = Number(t.amount) || 0;
      totalComm += c;
      totalAmount += a;
      if (!map[key]) map[key] = { count: 0, commission: 0, amount: 0 };
      map[key].count += 1;
      map[key].commission += c;
      map[key].amount += a;
    }
    return { map, totalComm, totalAmount };
  }, [rows]);

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 12 }}>
          Historial desde <code style={{ color: C.gold }}>transactions</code> con importe, comisión y tipo de envío. Filtro útil para contabilidad.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
            Desde{' '}
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              style={{ marginLeft: 6, padding: 6, borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.25)', color: '#fff' }}
            />
          </label>
          <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
            Hasta{' '}
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              style={{ marginLeft: 6, padding: 6, borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.25)', color: '#fff' }}
            />
          </label>
          <select
            value={filterDone}
            onChange={e => setFilterDone(e.target.value)}
            style={{ padding: 8, borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 12 }}
          >
            <option value="completed">Solo completadas</option>
            <option value="all">Todas (en rango)</option>
          </select>
          <button type="button" onClick={load} style={{ ...btnMini, marginLeft: 'auto' }} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
        </div>
      </DCard>

      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Resumen comisiones (vista actual)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
          Importe ventas:{' '}
          <strong style={{ color: C.gold }}>{commissionByShipping.totalAmount.toFixed(2)} €</strong> · Comisiones:{' '}
          <strong style={{ color: C.gold }}>{commissionByShipping.totalComm.toFixed(2)} €</strong> · {rows.length} filas
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(commissionByShipping.map).map(([k, v]) => (
            <div
              key={k}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 6 }}
            >
              <span style={{ color: 'rgba(255,255,255,0.75)' }}>
                {k} <span style={{ color: 'rgba(255,255,255,0.35)' }}>({v.count})</span>
              </span>
              <span style={{ color: C.gold, fontWeight: 800 }}>
                {v.commission.toFixed(2)} € <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>/ {v.amount.toFixed(2)} €</span>
              </span>
            </div>
          ))}
          {!Object.keys(commissionByShipping.map).length ? (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sin datos en el rango.</div>
          ) : null}
        </div>
      </DCard>

      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1100 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px' }}>Fecha</th>
                <th style={{ padding: '10px 12px' }}>Listing</th>
                <th style={{ padding: '10px 12px' }}>Comprador</th>
                <th style={{ padding: '10px 12px' }}>Vendedor</th>
                <th style={{ padding: '10px 12px' }}>Importe</th>
                <th style={{ padding: '10px 12px' }}>Comisión</th>
                <th style={{ padding: '10px 12px' }}>Envío</th>
                <th style={{ padding: '10px 12px' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                    {t.created_at ? new Date(t.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', maxWidth: 280 }}>
                    <div style={{ fontWeight: 700 }}>{t.listing?.title || '—'}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{t.listing?.artist || ''}</div>
                  </td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{t.buyerName}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{t.sellerName}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800 }}>{Number(t.amount || 0).toFixed(2)} €</td>
                  <td style={{ padding: '10px 12px', color: C.gold }}>{Number(t.commission || 0).toFixed(2)} €</td>
                  <td style={{ padding: '10px 12px', fontSize: 10 }}>{t.shipping_type || '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin transacciones.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminAuditoriaPanel({ onError }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [actionQ, setActionQ] = useState('');
  const [actorQ, setActorQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const finp = {
    padding: '8px 10px',
    borderRadius: 8,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 12,
  };

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      let q = supabase
        .from('admin_activity_log')
        .select(
          'id, created_at, action, entity_type, entity_id, summary, metadata, actor_id, actor_label, user_agent, ip'
        )
        .order('created_at', { ascending: false })
        .limit(400);
      const aq = actionQ.trim();
      if (aq) q = q.ilike('action', `%${aq.replace(/%/g, '')}%`);
      const ac = actorQ.trim();
      if (ac) q = q.ilike('actor_label', `%${ac.replace(/%/g, '')}%`);
      if (dateFrom.trim()) q = q.gte('created_at', new Date(dateFrom.trim()).toISOString());
      if (dateTo.trim()) {
        const d = new Date(dateTo.trim());
        d.setHours(23, 59, 59, 999);
        q = q.lte('created_at', d.toISOString());
      }
      const { data, error } = await q;
      if (error) throw error;
      setRows(data ?? []);
    } catch (e) {
      onError(e?.message || 'No se pudo cargar el log de auditoría');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError, actionQ, actorQ, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const esc = v => {
      const s = v == null ? '' : String(v);
      if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const headers = ['created_at', 'action', 'summary', 'actor_label', 'actor_id', 'entity_type', 'entity_id', 'ip', 'user_agent'];
    const lines = [headers.join(',')];
    for (const r of rows) {
      lines.push(headers.map(h => esc(r[h])).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin_activity_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Registro de acciones (<code style={{ color: C.gold }}>admin_activity_log</code>). Exporta el resultado filtrado (máx. 400 filas por consulta).
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Acción (contiene)
            <input value={actionQ} onChange={e => setActionQ(e.target.value)} placeholder="listing_paused…" style={{ ...finp, minWidth: 160 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Actor (contiene)
            <input value={actorQ} onChange={e => setActorQ(e.target.value)} placeholder="email o etiqueta…" style={{ ...finp, minWidth: 160 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Desde
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Hasta
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={finp} />
          </label>
          <button type="button" onClick={() => load()} style={btnMini} disabled={loading}>
            Aplicar
          </button>
          <button type="button" onClick={load} style={btnMini} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
          <button type="button" onClick={exportCsv} style={{ ...btnMini, marginLeft: 'auto' }} disabled={!rows.length}>
            Exportar CSV
          </button>
        </div>
      </DCard>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 1200 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px' }}>Fecha</th>
                <th style={{ padding: '10px 12px' }}>Acción</th>
                <th style={{ padding: '10px 12px' }}>Resumen</th>
                <th style={{ padding: '10px 12px' }}>Actor</th>
                <th style={{ padding: '10px 12px' }}>Entidad</th>
                <th style={{ padding: '10px 12px' }}>IP</th>
                <th style={{ padding: '10px 12px' }}>User-Agent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.75)' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.action}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 360, color: 'rgba(255,255,255,0.82)' }}>{r.summary || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.72)' }}>
                    {r.actor_label || (r.actor_id ? `${String(r.actor_id).slice(0, 8)}…` : '—')}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 9, color: 'rgba(255,255,255,0.55)' }}>
                    {r.entity_type ? `${r.entity_type}${r.entity_id ? ` · ${String(r.entity_id).slice(0, 8)}…` : ''}` : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: 'rgba(255,255,255,0.65)' }}>{r.ip || '—'}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 280, wordBreak: 'break-word', color: 'rgba(255,255,255,0.5)' }}>
                    {r.user_agent || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin entradas.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminEmailsPanel({ user, onError, onBadgesRefresh, menuBadges }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ queued: 0, sent: 0, failed: 0 });
  const [filtTo, setFiltTo] = useState('');
  const [filtStatus, setFiltStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busyId, setBusyId] = useState(null);

  const failedRows = useMemo(() => rows.filter(r => r.status === 'failed'), [rows]);
  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.EMAIL_FAILED,
    rows: failedRows,
    onBadgesRefresh,
    onError,
  });

  const finp = {
    padding: '8px 10px',
    borderRadius: 8,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 12,
  };

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const [queued, sent, failed] = await Promise.all([
        supabase.from('email_outbox').select('id', { count: 'exact', head: true }).eq('status', 'queued'),
        supabase.from('email_outbox').select('id', { count: 'exact', head: true }).eq('status', 'sent'),
        supabase.from('email_outbox').select('id', { count: 'exact', head: true }).eq('status', 'failed'),
      ]);
      const pick = r => (r.error ? 0 : r.count ?? 0);
      setStats({ queued: pick(queued), sent: pick(sent), failed: pick(failed) });

      let q = supabase
        .from('email_outbox')
        .select('id, created_at, kind, provider, "from", "to", subject, status, provider_message_id, error, meta')
        .order('created_at', { ascending: false })
        .limit(200);
      const em = filtTo.trim();
      if (em) q = q.ilike('to', `%${em}%`);
      const st = filtStatus.trim();
      if (st) q = q.eq('status', st);
      if (dateFrom.trim()) q = q.gte('created_at', new Date(dateFrom.trim()).toISOString());
      if (dateTo.trim()) {
        const d = new Date(dateTo.trim());
        d.setHours(23, 59, 59, 999);
        q = q.lte('created_at', d.toISOString());
      }
      const { data, error } = await q;
      if (error) throw error;
      setRows(data ?? []);
    } catch (e) {
      onError(e?.message || 'No se pudo cargar email_outbox');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError, filtTo, filtStatus, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const requeueFailed = async row => {
    if (!row?.id || row.status !== 'failed') return;
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase
        .from('email_outbox')
        .update({ status: 'queued', error: null, provider_message_id: null })
        .eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo re-colar el envío (¿política email_outbox_update_admin aplicada?)');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Auditoría de envíos en <code style={{ color: C.gold }}>email_outbox</code>. Los totales son globales; la tabla respeta filtros. «Reintentar» vuelve a poner <code style={{ color: C.gold }}>queued</code> (un worker/cron debe consumir la cola).
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 12, fontSize: 12, color: '#fff' }}>
          <span>
            En cola: <strong style={{ color: C.gold }}>{stats.queued}</strong>
          </span>
          <span>
            Enviados: <strong style={{ color: C.gold }}>{stats.sent}</strong>
          </span>
          <span>
            Fallidos: <strong style={{ color: stats.failed ? C.danger : C.gold }}>{stats.failed}</strong>
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Destinatario (contiene)
            <input value={filtTo} onChange={e => setFiltTo(e.target.value)} placeholder="correo…" style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Estado
            <select value={filtStatus} onChange={e => setFiltStatus(e.target.value)} style={{ ...finp, minWidth: 120 }}>
              <option value="">Todos</option>
              <option value="queued">queued</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Desde
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Hasta
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={finp} />
          </label>
          <button type="button" onClick={() => load()} style={btnMini} disabled={loading}>
            Aplicar
          </button>
          <AdminSeguimientoMarcarTodas
            unreadCount={adminMenuBadgeCount(menuBadges, 'emails')}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
          <button type="button" onClick={load} style={{ ...btnMini, marginLeft: 'auto' }} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
        </div>
      </DCard>
      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 1180 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                <th style={{ padding: '10px 12px' }}>Fecha</th>
                <th style={{ padding: '10px 12px' }}>Estado</th>
                <th style={{ padding: '10px 12px' }}>Para</th>
                <th style={{ padding: '10px 12px' }}>Asunto</th>
                <th style={{ padding: '10px 12px' }}>Kind</th>
                <th style={{ padding: '10px 12px' }}>Provider</th>
                <th style={{ padding: '10px 12px' }}>Error</th>
                <th style={{ padding: '10px 12px' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const track = r.status === 'failed';
                const sinLeer = track && seguimiento.isUnread(r.id);
                return (
                <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                    {track ? (
                      <AdminLeidaCell
                        leida={seguimiento.readIds.has(r.id)}
                        busy={seguimiento.readBusyId === r.id}
                        onChange={checked => seguimiento.setReadState(r.id, checked)}
                      />
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.75)' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 800 }}>{r.status}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 200, wordBreak: 'break-all' }}>{r.to}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 280 }}>{r.subject || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>{r.kind}</td>
                  <td style={{ padding: '10px 12px' }}>{r.provider}</td>
                  <td style={{ padding: '10px 12px', color: r.error ? C.danger : 'rgba(255,255,255,0.45)', maxWidth: 220 }}>
                    {r.error || '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {r.status === 'failed' ? (
                      <button type="button" style={btnMini} disabled={busyId === r.id} onClick={() => requeueFailed(r)}>
                        {busyId === r.id ? '…' : 'Reintentar'}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin registros de email.</div> : null}
        </div>
      </DCard>
    </div>
  );
}

function AdminDirectorioPartnersPanel({ onError }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filtSector, setFiltSector] = useState('');
  const [filtCity, setFiltCity] = useState('');
  const [filtRole, setFiltRole] = useState('');
  const [view, setView] = useState('cards');
  const [busyId, setBusyId] = useState(null);

  const finp = {
    padding: '8px 10px',
    borderRadius: 8,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 12,
  };

  const sectorOptions = [
    '',
    'tienda_discos',
    'sello',
    'festival',
    'audio',
    'editorial',
    'streaming',
    'artista',
    'venue',
    'otro',
  ];

  const roleOptions = ['', 'store', 'label', 'artist', 'promoter', 'supplier', 'pickup', 'venue', 'advertiser'];

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      let q = supabase
        .from('partners')
        .select(
          'id, code, company_name, sector, city, country, contact_email, contact_phone, verification_status, featured_in_directory, logo_url, created_at, partner_roles ( role, is_active )'
        )
        .eq('verification_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(120);
      if (filtSector.trim()) q = q.eq('sector', filtSector.trim());
      if (filtCity.trim()) q = q.ilike('city', `%${filtCity.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      let list = data ?? [];
      const fr = filtRole.trim();
      if (fr) {
        list = list.filter(p => (p.partner_roles || []).some(r => r.is_active !== false && String(r.role) === fr));
      }
      list = [...list].sort((a, b) => {
        const fa = a.featured_in_directory ? 1 : 0;
        const fb = b.featured_in_directory ? 1 : 0;
        if (fb !== fa) return fb - fa;
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setRows(list);
    } catch (e) {
      onError(e?.message || 'No se pudo cargar directorio partners');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onError, filtSector, filtCity, filtRole]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFeatured = async p => {
    if (!p?.id) return;
    setBusyId(p.id);
    onError('');
    try {
      const next = !p.featured_in_directory;
      const { error } = await supabase.from('partners').update({ featured_in_directory: next }).eq('id', p.id);
      if (error) throw error;
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar destacado (¿migración 20260512150000 aplicada?)');
    } finally {
      setBusyId(null);
    }
  };

  const rolesLabel = prs =>
    (prs || [])
      .filter(r => r.is_active !== false)
      .map(r => r.role)
      .join(', ') || '—';

  return (
    <div>
      <DCard style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 10 }}>
          Partners verificados. <strong>Destacar</strong> marca prioridad en <code style={{ color: C.gold }}>partners_public</code> (listados futuros / mapa). Filtro de rol se aplica en cliente sobre roles activos.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Sector
            <select value={filtSector} onChange={e => setFiltSector(e.target.value)} style={{ ...finp, minWidth: 140 }}>
              {sectorOptions.map(s => (
                <option key={s || 'all'} value={s}>
                  {s || 'Todos'}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Ciudad (contiene)
            <input value={filtCity} onChange={e => setFiltCity(e.target.value)} placeholder="Barcelona…" style={finp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            Rol activo
            <select value={filtRole} onChange={e => setFiltRole(e.target.value)} style={{ ...finp, minWidth: 130 }}>
              {roleOptions.map(s => (
                <option key={s || 'all'} value={s}>
                  {s || 'Todos'}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => load()} style={btnMini} disabled={loading}>
            Aplicar
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => setView('cards')}
              style={{
                ...btnMini,
                borderColor: view === 'cards' ? C.gold : 'rgba(255,255,255,0.12)',
                opacity: view === 'cards' ? 1 : 0.75,
              }}
            >
              Tarjetas
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              style={{
                ...btnMini,
                borderColor: view === 'table' ? C.gold : 'rgba(255,255,255,0.12)',
                opacity: view === 'table' ? 1 : 0.75,
              }}
            >
              Tabla
            </button>
            <button type="button" onClick={load} style={btnMini} disabled={loading}>
              {loading ? '…' : 'Recargar'}
            </button>
          </div>
        </div>
      </DCard>
      {view === 'cards' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {rows.map(p => (
            <DCard key={p.id} style={{ padding: 14 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt=""
                    style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 10, border: '0.5px solid rgba(255,255,255,0.12)' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.35)',
                    }}
                  >
                    —
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: C.gold, fontSize: 11 }}>{p.code}</div>
                  <div style={{ fontWeight: 800, color: '#fff', fontSize: 13, marginTop: 4, lineHeight: 1.25 }}>{p.company_name}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>
                    {p.sector || '—'} · {p.city || '—'}
                    {p.country ? `, ${p.country}` : ''}
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>Roles: {rolesLabel(p.partner_roles)}</div>
                  {p.featured_in_directory ? (
                    <div style={{ marginTop: 8, fontSize: 10, fontWeight: 800, color: C.gold }}>Destacado en directorio</div>
                  ) : null}
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button type="button" style={btnMini} disabled={busyId === p.id} onClick={() => toggleFeatured(p)}>
                  {busyId === p.id ? '…' : p.featured_in_directory ? 'Quitar destacado' : 'Destacar'}
                </button>
              </div>
            </DCard>
          ))}
        </div>
      ) : (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1000 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px' }}>Código</th>
                  <th style={{ padding: '10px 12px' }}>Negocio</th>
                  <th style={{ padding: '10px 12px' }}>Sector</th>
                  <th style={{ padding: '10px 12px' }}>Ciudad</th>
                  <th style={{ padding: '10px 12px' }}>Roles</th>
                  <th style={{ padding: '10px 12px' }}>Dest.</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 900, color: C.gold }}>{p.code}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 800 }}>{p.company_name}</td>
                    <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{p.sector || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{p.city || '—'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>{rolesLabel(p.partner_roles)}</td>
                    <td style={{ padding: '10px 12px' }}>{p.featured_in_directory ? 'Sí' : '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <button type="button" style={btnMini} disabled={busyId === p.id} onClick={() => toggleFeatured(p)}>
                        {busyId === p.id ? '…' : p.featured_in_directory ? 'Quitar' : 'Destacar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DCard>
      )}
      {!rows.length && !loading ? (
        <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin partners con estos filtros.</div>
      ) : null}
    </div>
  );
}

function AdminConfigPanel({ onError }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [legal, setLegal] = useState({
    aviso: { replace: false, text: '' },
    privacidad: { replace: false, text: '' },
    terminos: { replace: false, text: '' },
    cookies: { replace: false, text: '' },
  });
  const [fees, setFees] = useState({ listingCommissionPercent: 8, note: '' });
  const [flags, setFlags] = useState({ showPartnerDirectory: true, showEventsSection: true });
  const [partnerTicketSalesEnabled, setPartnerTicketSalesEnabled] = useState(false);

  const mergeLegal = raw => {
    const base = {
      aviso: { replace: false, text: '' },
      privacidad: { replace: false, text: '' },
      terminos: { replace: false, text: '' },
      cookies: { replace: false, text: '' },
    };
    if (!raw || typeof raw !== 'object') return base;
    for (const k of Object.keys(base)) {
      const o = raw[k];
      if (o && typeof o === 'object') {
        base[k] = {
          replace: !!o.replace,
          text: typeof o.text === 'string' ? o.text : '',
        };
      }
    }
    return base;
  };

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const { data, error } = await supabase
        .from('app_config')
        .select('key, value')
        .in('key', ['legal_overrides', 'fees_marketplace', 'feature_flags', 'partner_ticket_sales']);
      if (error) throw error;
      const map = Object.fromEntries((data ?? []).map(r => [r.key, r.value]));
      setLegal(mergeLegal(map.legal_overrides));
      const fm = map.fees_marketplace;
      setFees({
        listingCommissionPercent: Math.min(50, Math.max(0, Number(fm?.listingCommissionPercent) || 8)),
        note: typeof fm?.note === 'string' ? fm.note : '',
      });
      const ff = map.feature_flags;
      setFlags({
        showPartnerDirectory: ff?.showPartnerDirectory !== false,
        showEventsSection: ff?.showEventsSection !== false,
      });
      const pts = map.partner_ticket_sales;
      setPartnerTicketSalesEnabled(pts?.enabled === true);
    } catch (e) {
      onError(e?.message || 'No se pudo cargar app_config');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const saveAll = async () => {
    setSaving(true);
    onError('');
    try {
      const legalPayload = {
        aviso: { replace: !!legal.aviso.replace, text: String(legal.aviso.text || '') },
        privacidad: { replace: !!legal.privacidad.replace, text: String(legal.privacidad.text || '') },
        terminos: { replace: !!legal.terminos.replace, text: String(legal.terminos.text || '') },
        cookies: { replace: !!legal.cookies.replace, text: String(legal.cookies.text || '') },
      };
      const feesPayload = {
        listingCommissionPercent: Math.min(50, Math.max(0, Number(fees.listingCommissionPercent) || 0)),
        note: String(fees.note || '').trim() || null,
      };
      const flagsPayload = {
        showPartnerDirectory: !!flags.showPartnerDirectory,
        showEventsSection: !!flags.showEventsSection,
      };
      const { error: e1 } = await supabase.from('app_config').upsert({ key: 'legal_overrides', value: legalPayload }, { onConflict: 'key' });
      if (e1) throw e1;
      const { error: e2 } = await supabase.from('app_config').upsert({ key: 'fees_marketplace', value: feesPayload }, { onConflict: 'key' });
      if (e2) throw e2;
      const { error: e3 } = await supabase.from('app_config').upsert({ key: 'feature_flags', value: flagsPayload }, { onConflict: 'key' });
      if (e3) throw e3;
      const { error: e4 } = await supabase.from('app_config').upsert(
        {
          key: 'partner_ticket_sales',
          value: {
            enabled: !!partnerTicketSalesEnabled,
            note: 'Interruptor global: venta de entradas en Comunidad y módulo partner Entradas.',
          },
        },
        { onConflict: 'key' }
      );
      if (e4) throw e4;
      invalidatePartnerTicketSalesConfigCache();
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo guardar (¿claves creadas en BD?)');
    } finally {
      setSaving(false);
    }
  };

  const ta = {
    width: '100%',
    minHeight: 100,
    padding: 10,
    borderRadius: 10,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 12,
    fontFamily: 'inherit',
    resize: 'vertical',
  };

  const legalLabels = {
    aviso: 'Aviso legal',
    privacidad: 'Privacidad',
    terminos: 'Términos',
    cookies: 'Cookies',
  };

  if (loading) {
    return (
      <DCard style={{ padding: 18 }}>
        <div style={{ color: 'rgba(255,255,255,0.45)' }}>Cargando configuración…</div>
      </DCard>
    );
  }

  return (
    <div>
      <DCard style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Configuración global</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 14 }}>
          Claves en <code style={{ color: C.gold }}>app_config</code> con whitelist. Los textos legales se muestran en las páginas públicas según el modo; la comisión es referencia hasta integrar cobro automático.
        </div>
        <BtnGold type="button" style={{ width: 'auto', minWidth: 160 }} onClick={saveAll} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar todo'}
        </BtnGold>
      </DCard>

      <DCard style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Textos legales (override)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14, lineHeight: 1.5 }}>
          Texto plano (sin HTML). Si «Sustituir página» está marcado y hay texto, solo se muestra tu texto. Si no, se muestra el texto legal por defecto y tu bloque como sección adicional al final.
        </div>
        {(['aviso', 'privacidad', 'terminos', 'cookies']).map(k => (
          <div key={k} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.gold, marginBottom: 8 }}>{legalLabels[k]}</div>
            <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!legal[k].replace}
                onChange={e => setLegal(prev => ({ ...prev, [k]: { ...prev[k], replace: e.target.checked } }))}
              />
              Sustituir página completa por este texto
            </label>
            <textarea
              value={legal[k].text}
              onChange={e => setLegal(prev => ({ ...prev, [k]: { ...prev[k], text: e.target.value } }))}
              style={ta}
              placeholder="Escribe aquí el texto que quieres publicar…"
            />
          </div>
        ))}
      </DCard>

      <DCard style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Comisiones (referencia)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 }}>
          Porcentaje orientativo de comisión marketplace (0–50). El cobro efectivo puede seguir viniendo de cada transacción hasta unificar reglas.
        </div>
        <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
          Comisión listados (%)
          <input
            type="number"
            min={0}
            max={50}
            step={0.5}
            value={fees.listingCommissionPercent}
            onChange={e => setFees(f => ({ ...f, listingCommissionPercent: e.target.value }))}
            style={{ ...ta, minHeight: 0, width: 120, marginTop: 6 }}
          />
        </label>
        <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 12 }}>
          Nota interna
          <textarea value={fees.note} onChange={e => setFees(f => ({ ...f, note: e.target.value }))} style={{ ...ta, minHeight: 60, marginTop: 6 }} placeholder="Opcional" />
        </label>
      </DCard>

      <DCard style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Venta de entradas (partners)</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 }}>
          Si está desactivado, nadie puede publicar enlaces de venta en Comunidad (salvo moderación admin). En Partners → Aprobados activa suscripción y venta por partner (rol Promotor o Venue).
        </div>
        <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={partnerTicketSalesEnabled}
            onChange={e => setPartnerTicketSalesEnabled(e.target.checked)}
          />
          Habilitar venta de entradas en la plataforma (global)
        </label>
      </DCard>

      <DCard style={{ padding: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Flags de producto</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 }}>
          La app puede leer <code style={{ color: C.gold }}>feature_flags</code> donde ya esté cableado; si no, quedan documentados aquí.
        </div>
        <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={flags.showPartnerDirectory}
            onChange={e => setFlags(f => ({ ...f, showPartnerDirectory: e.target.checked }))}
          />
          Mostrar directorio partners (cuando exista en UI)
        </label>
        <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={flags.showEventsSection} onChange={e => setFlags(f => ({ ...f, showEventsSection: e.target.checked }))} />
          Mostrar sección eventos (cuando exista en UI)
        </label>
      </DCard>
    </div>
  );
}

/** Listados admin: anuncio + perfil vendedor (partner_active_roles, ubicación INE). */
const LISTING_ADMIN_SELECT_FIELDS =
  'id, title, artist, price, status, format, views, created_at, seller_id, cover_url, photos, is_auction, pais, ciudad, localidad, profiles:seller_id ( username, pais, ciudad, localidad, partner_active_roles )';

function AdminListingsPanel({
  user,
  onError,
  onBadgesRefresh,
  menuBadges,
  onGoAdminSection,
  onViewListingFicha,
  fichaLoadingId,
}) {
  const [tab, setTab] = useState('activos');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [socialKitOpen, setSocialKitOpen] = useState(null);
  const [socialNetById, setSocialNetById] = useState({});
  const [activos, setActivos] = useState([]);
  const [activosLoading, setActivosLoading] = useState(false);
  const [top, setTop] = useState([]);
  const [reports, setReports] = useState([]);
  const [browse, setBrowse] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [filtStatus, setFiltStatus] = useState('');
  const [filtFormat, setFiltFormat] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');

  const showReportSeguimiento = tab === 'reportados';
  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.LISTING_REPORT,
    rows: showReportSeguimiento ? reports : [],
    onBadgesRefresh,
    onError,
  });

  const listingThumbUrl = l => {
    if (!l) return null;
    if (l.cover_url && String(l.cover_url).trim()) return String(l.cover_url).trim();
    const ph = l.photos;
    if (Array.isArray(ph) && ph.length && String(ph[0]).trim()) return String(ph[0]).trim();
    return null;
  };

  const profileIsPartner = prof =>
    Array.isArray(prof?.partner_active_roles) && prof.partner_active_roles.length > 0;

  const formatListingUbicacion = (listing, prof) => {
    const fromListing = [listing?.pais, listing?.ciudad, listing?.localidad]
      .map(x => (x != null ? String(x).trim() : ''))
      .filter(Boolean);
    if (fromListing.length) return fromListing.join(' · ');
    const fromProfile = [prof?.pais, prof?.ciudad, prof?.localidad]
      .map(x => (x != null ? String(x).trim() : ''))
      .filter(Boolean);
    if (fromProfile.length) return fromProfile.join(' · ');
    return '—';
  };

  const refreshSocialNetworks = useCallback(async listings => {
    const ids = (listings || []).map(l => l?.id).filter(Boolean);
    if (!ids.length) {
      setSocialNetById({});
      return;
    }
    const map = await fetchSocialNetworksByContentIds(ids);
    setSocialNetById(map);
  }, []);

  const loadActivos = useCallback(async () => {
    setActivosLoading(true);
    onError('');
    try {
      const { data, error } = await supabase
        .from('listings')
        .select(LISTING_ADMIN_SELECT_FIELDS)
        .eq('status', 'active')
        .eq('is_auction', false)
        .order('created_at', { ascending: false })
        .limit(80);
      if (error) throw error;
      const rows = data ?? [];
      setActivos(rows);
      await refreshSocialNetworks(rows);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar anuncios activos');
      setActivos([]);
    } finally {
      setActivosLoading(false);
    }
  }, [onError, refreshSocialNetworks]);

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const topReq = supabase
        .from('listings')
        .select(LISTING_ADMIN_SELECT_FIELDS)
        .order('views', { ascending: false })
        .limit(20);

      const repReq = supabase
        .from('listing_reports')
        .select(
          `
          id,
          listing_id,
          reporter_id,
          category,
          body,
          status,
          created_at,
          reviewed_at,
          reviewed_by,
          listings:listing_id (
            id,
            title,
            artist,
            price,
            status,
            views,
            seller_id,
            is_auction,
            pais,
            ciudad,
            localidad,
            profiles:seller_id ( username, pais, ciudad, localidad, partner_active_roles )
          )
        `
        )
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(60);

      const [{ data: topRows, error: topErr }, { data: repRows, error: repErr }] = await Promise.all([topReq, repReq]);
      if (topErr) throw topErr;
      if (repErr) throw repErr;
      const topRowsSafe = topRows ?? [];
      const repSafe = repRows ?? [];
      setTop(topRowsSafe);
      setReports(repSafe);
      const socialIds = [
        ...topRowsSafe.map(l => l.id),
        ...repSafe.map(r => r.listings?.id).filter(Boolean),
      ];
      await refreshSocialNetworks(socialIds);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar listings');
      setTop([]);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [onError, refreshSocialNetworks]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 'activos') loadActivos();
  }, [tab, loadActivos]);

  const loadBrowse = useCallback(async () => {
    setBrowseLoading(true);
    onError('');
    try {
      let q = supabase.from('listings').select(LISTING_ADMIN_SELECT_FIELDS).order('created_at', { ascending: false }).limit(80);
      if (filtStatus.trim()) q = q.eq('status', filtStatus.trim());
      if (filtFormat.trim()) q = q.eq('format', filtFormat.trim());
      const pmin = priceMin.trim();
      const pmax = priceMax.trim();
      if (pmin !== '' && Number.isFinite(Number(pmin))) q = q.gte('price', Number(pmin));
      if (pmax !== '' && Number.isFinite(Number(pmax))) q = q.lte('price', Number(pmax));
      const { data, error } = await q;
      if (error) throw error;
      const rows = data ?? [];
      setBrowse(rows);
      await refreshSocialNetworks(rows);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar listings');
      setBrowse([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [onError, filtStatus, filtFormat, priceMin, priceMax, refreshSocialNetworks]);

  useEffect(() => {
    if (tab === 'explorar') loadBrowse();
  }, [tab, loadBrowse]);

  const reloadListingViews = useCallback(async () => {
    const tasks = [load()];
    if (tab === 'explorar') tasks.push(loadBrowse());
    if (tab === 'activos') tasks.push(loadActivos());
    await Promise.all(tasks);
  }, [tab, load, loadBrowse, loadActivos]);

  const pauseListing = async listingId => {
    if (!listingId) return;
    setBusy(listingId);
    onError('');
    try {
      await adminSetListingStatus(listingId, 'paused');
      await reloadListingViews();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo pausar');
    } finally {
      setBusy(null);
    }
  };

  const removeListing = async listingId => {
    if (!listingId) return;
    if (!window.confirm('¿Marcar este anuncio como retirado? Dejará de mostrarse en el marketplace.')) return;
    setBusy(listingId);
    onError('');
    try {
      await adminSetListingStatus(listingId, 'removed');
      await reloadListingViews();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo retirar');
    } finally {
      setBusy(null);
    }
  };

  const activateListing = async listingId => {
    if (!listingId) return;
    setBusy(listingId);
    onError('');
    try {
      await adminSetListingStatus(listingId, 'active');
      await reloadListingViews();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo activar');
    } finally {
      setBusy(null);
    }
  };

  const resolveReport = async (reportId, nextStatus) => {
    if (!reportId) return;
    setBusy(reportId);
    onError('');
    try {
      const patch = {
        status: nextStatus,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id ?? null,
      };
      const { error } = await supabase.from('listing_reports').update(patch).eq('id', reportId);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        action: nextStatus === 'dismissed' ? 'listing_report_dismissed' : 'listing_report_reviewed',
        entity_type: 'listing_report',
        entity_id: reportId,
        summary: `Reporte de listing ${nextStatus === 'dismissed' ? 'descartado' : 'revisado'}`,
        metadata: { status: nextStatus },
      });
      await reloadListingViews();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar el reporte');
    } finally {
      setBusy(null);
    }
  };

  const pills = [
    { id: 'activos', label: `En mercado (${activos.length})` },
    { id: 'reportados', label: `Reportados (${reports.length})` },
    { id: 'top', label: 'Más vistos' },
    { id: 'explorar', label: 'Explorar / filtros' },
  ];

  const reload = () => {
    if (tab === 'explorar') return loadBrowse();
    if (tab === 'activos') return loadActivos();
    return load();
  };
  const reloadBusy = tab === 'explorar' ? browseLoading : tab === 'activos' ? activosLoading : loading;

  const renderListingTableRow = (l, { keyPrefix = '' } = {}) => {
    const thumb = listingThumbUrl(l);
    return (
      <tr key={`${keyPrefix}${l.id}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
        <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
          {thumb ? (
            <img
              src={thumb}
              alt=""
              style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.12)' }}
            />
          ) : (
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.06)',
                fontSize: 9,
                color: 'rgba(255,255,255,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              —
            </div>
          )}
        </td>
        <td style={{ padding: '10px 12px', maxWidth: 260 }}>
          <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</div>
          <div style={{ marginTop: 3, fontSize: 10, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l.artist || '—'}
            {l.is_auction ? ' · Subasta' : ''}
          </div>
        </td>
        <td style={{ padding: '10px 12px', color: C.gold, whiteSpace: 'nowrap' }}>
          {l.profiles?.username ? `@${l.profiles.username}` : '—'}
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
          {profileIsPartner(l.profiles) ? (
            <span style={{ color: C.green, fontWeight: 900, fontSize: 14 }} title="Partner verificado (roles activos)">
              ✓
            </span>
          ) : (
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700 }}>NO</span>
          )}
        </td>
        <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.72)', lineHeight: 1.35, maxWidth: 220 }}>
          {formatListingUbicacion(l, l.profiles)}
        </td>
        <td style={{ padding: '10px 12px' }}>{l.price != null ? `${Number(l.price).toFixed(2)}€` : '—'}</td>
        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.55)' }}>{l.format || '—'}</td>
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{l.status || '—'}</td>
        <td style={{ padding: '10px 12px' }}>{l.views ?? 0}</td>
        <td style={{ padding: '10px 12px' }}>
          <AdminListingSocialActions
            listing={l}
            busy={busy === l.id || fichaLoadingId === l.id}
            socialNetworks={socialNetById[l.id] || []}
            onRedes={setSocialKitOpen}
            onActivate={activateListing}
            onPause={pauseListing}
            onRemove={removeListing}
            onViewFicha={onViewListingFicha}
          />
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {pills.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => setTab(p.id)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: `0.5px solid ${tab === p.id ? C.gold : 'rgba(255,255,255,0.12)'}`,
              background: tab === p.id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
              color: tab === p.id ? C.gold : 'rgba(255,255,255,0.65)',
              fontSize: 12,
              fontWeight: tab === p.id ? 700 : 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {p.id === 'reportados' ? (
              <AdminSeguimientoTabLabel label={p.label} unreadCount={adminMenuBadgeCount(menuBadges, 'listings')} />
            ) : (
              p.label
            )}
          </button>
        ))}
        {showReportSeguimiento ? (
          <AdminSeguimientoMarcarTodas
            unreadCount={adminMenuBadgeCount(menuBadges, 'listings')}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
        ) : null}
        <button type="button" onClick={reload} style={{ ...btnMini, marginLeft: 'auto' }} disabled={reloadBusy}>
          {reloadBusy ? 'Cargando…' : 'Recargar'}
        </button>
      </div>

      <DCard style={{ padding: 12, marginBottom: 14, background: 'rgba(120,160,255,0.06)', borderColor: 'rgba(120,160,255,0.22)' }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.78)', lineHeight: 1.55 }}>
          <strong style={{ color: 'rgba(180,200,255,0.95)' }}>Hub de redes</strong> — Igual que en Noticias y Comunidad: botón{' '}
          <strong>Redes</strong> abre el kit (copiar textos, enviar a n8n). Al activar un anuncio se encola automáticamente si{' '}
          <code style={{ color: C.gold }}>social_auto_enqueue</code> está activo. Cola global:{' '}
          {typeof onGoAdminSection === 'function' ? (
            <button
              type="button"
              onClick={() => onGoAdminSection('redes_cola')}
              style={{
                margin: 0,
                padding: 0,
                border: 'none',
                background: 'none',
                color: C.gold,
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: 'inherit',
              }}
            >
              Cola redes
            </button>
          ) : (
            'menú Contenido → Cola redes'
          )}
          .
        </div>
      </DCard>

      {reloadBusy && tab !== 'explorar' ? (
        <div style={{ color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
      ) : tab === 'activos' ? (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1240 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 56 }}>Foto</th>
                  <th style={{ padding: '10px 12px' }}>Anuncio</th>
                  <th style={{ padding: '10px 12px' }}>Vendedor</th>
                  <th style={{ padding: '10px 12px', width: 72, textAlign: 'center' }}>Partner</th>
                  <th style={{ padding: '10px 12px', minWidth: 160 }}>Ubicación</th>
                  <th style={{ padding: '10px 12px' }}>Precio</th>
                  <th style={{ padding: '10px 12px' }}>Formato</th>
                  <th style={{ padding: '10px 12px' }}>Estado</th>
                  <th style={{ padding: '10px 12px' }}>Views</th>
                  <th style={{ padding: '10px 12px', minWidth: 220 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>{activos.map(l => renderListingTableRow(l, { keyPrefix: 'act-' }))}</tbody>
            </table>
            {!activos.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay anuncios activos en mercado.</div>
            ) : null}
          </div>
        </DCard>
      ) : tab === 'reportados' ? (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1180 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 72 }}>Leída</th>
                  <th style={{ padding: '10px 12px' }}>Reporte</th>
                  <th style={{ padding: '10px 12px' }}>Listing</th>
                  <th style={{ padding: '10px 12px', width: 72, textAlign: 'center' }}>Partner</th>
                  <th style={{ padding: '10px 12px', minWidth: 160 }}>Ubicación</th>
                  <th style={{ padding: '10px 12px' }}>Motivo</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => {
                  const track = true;
                  const sinLeer = track && seguimiento.isUnread(r.id);
                  return (
                  <tr key={r.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <AdminLeidaCell
                        leida={seguimiento.readIds.has(r.id)}
                        busy={seguimiento.readBusyId === r.id}
                        onChange={checked => seguimiento.setReadState(r.id, checked)}
                      />
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, maxWidth: 280 }}>
                      <div style={{ fontWeight: 800, color: '#f2f2f2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.listings?.title ? r.listings.title : `${String(r.listing_id).slice(0, 8)}…`}
                      </div>
                      <div style={{ marginTop: 3, fontSize: 10, color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.listings?.artist ? r.listings.artist : '—'}
                        {r.listings?.profiles?.username ? ` · @${r.listings.profiles.username}` : ''}
                        {r.listings?.price != null ? ` · ${Number(r.listings.price).toFixed(2)}€` : ''}
                        {r.listings?.status ? ` · ${r.listings.status}` : ''}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {profileIsPartner(r.listings?.profiles) ? (
                        <span style={{ color: C.green, fontWeight: 900, fontSize: 14 }} title="Partner verificado (roles activos)">
                          ✓
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700 }}>NO</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.72)', lineHeight: 1.35, maxWidth: 200 }}>
                      {formatListingUbicacion(r.listings, r.listings?.profiles)}
                    </td>
                    <td style={{ padding: '10px 12px', maxWidth: 360 }}>
                      <div style={{ color: C.gold, fontWeight: 700, fontSize: 10 }}>{r.category}</div>
                      <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.78)', fontSize: 11, lineHeight: 1.35 }}>
                        {r.body?.trim() || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        {r.listings ? (
                          <AdminListingSocialActions
                            listing={{ ...r.listings, is_auction: r.listings.is_auction }}
                            busy={busy === r.listing_id || fichaLoadingId === r.listing_id}
                            socialNetworks={socialNetById[r.listing_id] || []}
                            onRedes={setSocialKitOpen}
                            onActivate={activateListing}
                            onPause={pauseListing}
                            onRemove={removeListing}
                            onViewFicha={onViewListingFicha}
                          />
                        ) : null}
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => resolveReport(r.id, 'reviewed')}>
                          Revisado
                        </button>
                        <button type="button" style={btnMini} disabled={busy === r.id} onClick={() => resolveReport(r.id, 'dismissed')}>
                          Descartar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            {!reports.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay reportes abiertos.</div>
            ) : null}
          </div>
        </DCard>
      ) : tab === 'top' ? (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1240 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 56 }}>Foto</th>
                  <th style={{ padding: '10px 12px' }}>Anuncio</th>
                  <th style={{ padding: '10px 12px' }}>Vendedor</th>
                  <th style={{ padding: '10px 12px', width: 72, textAlign: 'center' }}>Partner</th>
                  <th style={{ padding: '10px 12px', minWidth: 160 }}>Ubicación</th>
                  <th style={{ padding: '10px 12px' }}>Precio</th>
                  <th style={{ padding: '10px 12px' }}>Formato</th>
                  <th style={{ padding: '10px 12px' }}>Estado</th>
                  <th style={{ padding: '10px 12px' }}>Views</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>{top.map(l => renderListingTableRow(l, { keyPrefix: 'top-' }))}</tbody>
            </table>
            {!top.length ? <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay listings.</div> : null}
          </div>
        </DCard>
      ) : (
        <DCard style={{ padding: 14, overflow: 'hidden' }}>
          {browseLoading ? (
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginBottom: 10 }}>Actualizando listado…</div>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
              Estado
              <select
                value={filtStatus}
                onChange={e => setFiltStatus(e.target.value)}
                style={{
                  minWidth: 130,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontSize: 12,
                }}
              >
                <option value="">Todos</option>
                <option value="active">active</option>
                <option value="paused">paused</option>
                <option value="removed">removed</option>
                <option value="sold">sold</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
              Formato (exacto)
              <input
                value={filtFormat}
                onChange={e => setFiltFormat(e.target.value)}
                placeholder="vinilo, cd…"
                style={{
                  minWidth: 120,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontSize: 12,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
              Precio min (€)
              <input
                value={priceMin}
                onChange={e => setPriceMin(e.target.value)}
                inputMode="decimal"
                style={{
                  width: 88,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontSize: 12,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
              Precio max (€)
              <input
                value={priceMax}
                onChange={e => setPriceMax(e.target.value)}
                inputMode="decimal"
                style={{
                  width: 88,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontSize: 12,
                }}
              />
            </label>
            <button type="button" onClick={() => loadBrowse()} style={btnMini} disabled={browseLoading}>
              Aplicar filtros
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1240 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 56 }}>Foto</th>
                  <th style={{ padding: '10px 12px' }}>Anuncio</th>
                  <th style={{ padding: '10px 12px' }}>Vendedor</th>
                  <th style={{ padding: '10px 12px', width: 72, textAlign: 'center' }}>Partner</th>
                  <th style={{ padding: '10px 12px', minWidth: 160 }}>Ubicación</th>
                  <th style={{ padding: '10px 12px' }}>Precio</th>
                  <th style={{ padding: '10px 12px' }}>Formato</th>
                  <th style={{ padding: '10px 12px' }}>Estado</th>
                  <th style={{ padding: '10px 12px' }}>Views</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>{browse.map(l => renderListingTableRow(l, { keyPrefix: 'br-' }))}</tbody>
            </table>
            {!browse.length && !browseLoading ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin resultados con estos filtros.</div>
            ) : null}
          </div>
        </DCard>
      )}
      <AdminSocialKitModal
        open={!!socialKitOpen}
        onClose={() => setSocialKitOpen(null)}
        contentType={socialKitOpen?.contentType ?? 'listing'}
        contentId={socialKitOpen?.contentId ?? ''}
        titleHint={socialKitOpen?.titleHint ?? ''}
        onError={onError}
      />
    </div>
  );
}

function AdminDashboard({ user, onError, onGo, menuBadges }) {
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [counts, setCounts] = useState({
    users_total: null,
    users_week: null,
    partners_pending: null,
    partners_reviewing: null,
    noticias_borradores: null,
    ads_activos: null,
    unread_partner_threads: null,
  });

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    onError('');
    try {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [
        usersTotal,
        usersWeek,
        partnersPending,
        partnersReviewing,
        noticiasBorradores,
        adsActivos,
      ] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', weekAgo),
        supabase.from('partners').select('id', { count: 'exact', head: true }).eq('verification_status', 'pending'),
        supabase.from('partners').select('id', { count: 'exact', head: true }).eq('verification_status', 'reviewing'),
        supabase.from('noticias_musica').select('id', { count: 'exact', head: true }).eq('publicado', false),
        supabase
          .from('ads')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'activo')
          .lte('starts_at', new Date().toISOString())
          .gte('ends_at', new Date().toISOString()),
      ]);

      // Hilos sin leer (partner_support): conversaciones con mensajes nuevos desde last_read_at (o sin fila reads).
      // Nota: se calcula en cliente con 2 consultas para evitar RPC: lista de convs + reads + último msg por conv.
      let unreadThreads = 0;
      const { data: convs, error: convErr } = await supabase
        .from('conversations')
        .select('id, updated_at')
        .eq('conversation_type', 'partner_support')
        .order('updated_at', { ascending: false })
        .limit(200);
      if (!convErr && convs?.length) {
        const convIds = convs.map(c => c.id);
        const { data: reads } = await supabase
          .from('conversation_reads')
          .select('conversation_id, last_read_at')
          .eq('reader_id', user.id)
          .in('conversation_id', convIds);
        const readMap = Object.fromEntries((reads ?? []).map(r => [r.conversation_id, r.last_read_at]));

        // Exacto: tomamos el último mensaje por conversación NO enviado por el admin.
        // Si ese mensaje es posterior a last_read_at (o no hay last_read_at), el hilo cuenta como "sin leer".
        const { data: lastMsgs, error: msgErr } = await supabase
          .from('messages')
          .select('conversation_id, created_at, sender_id')
          .in('conversation_id', convIds)
          .neq('sender_id', user.id)
          .order('created_at', { ascending: false })
          .limit(800);

        if (!msgErr && lastMsgs?.length) {
          const lastByConv = {};
          for (const m of lastMsgs) {
            const cid = m?.conversation_id;
            if (!cid) continue;
            if (lastByConv[cid]) continue; // ya tenemos el último (por orden desc)
            lastByConv[cid] = m.created_at;
          }
          unreadThreads = convIds.filter(cid => {
            const lastOtherAt = lastByConv[cid];
            if (!lastOtherAt) return false; // no hay mensajes de la otra parte
            const lr = readMap[cid];
            if (!lr) return true;
            return new Date(lastOtherAt) > new Date(lr);
          }).length;
        } else {
          unreadThreads = 0;
        }
      }

      const next = {
        users_total: usersTotal.count ?? 0,
        users_week: usersWeek.count ?? 0,
        partners_pending: partnersPending.count ?? 0,
        partners_reviewing: partnersReviewing.count ?? 0,
        noticias_borradores: noticiasBorradores.count ?? 0,
        ads_activos: adsActivos.count ?? 0,
        unread_partner_threads: unreadThreads,
      };

      setCounts(next);
    } catch (e) {
      onError(e?.message || 'No se pudieron cargar métricas');
    } finally {
      setLoading(false);
    }
  }, [onError, user?.id]);

  const loadActivity = useCallback(async () => {
    if (!user?.id) return;
    setActivityLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_activity_log')
        .select('id, created_at, action, entity_type, entity_id, summary, metadata, actor_id, actor_label')
        .order('created_at', { ascending: false })
        .limit(18);
      if (!error) setActivity(data ?? []);
    } finally {
      setActivityLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
    loadActivity();
  }, [load, loadActivity]);

  const card = (title, value, sub, actionId, pendingAlert = 0) => (
    <DCard
      style={{
        padding: 16,
        background: 'rgba(255,255,255,0.03)',
        borderColor: pendingAlert > 0 ? 'rgba(226,75,74,0.35)' : 'rgba(255,255,255,0.08)',
        minWidth: 220,
        flex: '1 1 240px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.08em',
          color: 'rgba(255,255,255,0.45)',
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {pendingAlert > 0 ? <AdminAlertDot title={`${pendingAlert} pendiente(s)`} /> : null}
        <span>{title}</span>
        {pendingAlert > 0 ? <AdminAlertCount count={pendingAlert} /> : null}
      </div>
      <div style={{ marginTop: 8, fontSize: 26, fontWeight: 800, color: C.gold, lineHeight: 1.1 }}>
        {loading ? '—' : value ?? '—'}
      </div>
      {sub ? (
        <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.35 }}>{sub}</div>
      ) : null}
      {actionId ? (
        <button
          type="button"
          onClick={() => onGo?.(actionId)}
          style={{
            marginTop: 12,
            padding: '8px 10px',
            borderRadius: 10,
            border: '0.5px solid rgba(200,168,75,0.25)',
            background: 'rgba(200,168,75,0.08)',
            color: C.gold,
            fontWeight: 700,
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          Abrir
        </button>
      ) : null}
    </DCard>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {card('USUARIOS', counts.users_total, `Nuevos esta semana: ${loading ? '—' : counts.users_week ?? 0}`, 'usuarios')}
        {card(
          'PARTNERS',
          loading ? '—' : (counts.partners_pending ?? 0) + (counts.partners_reviewing ?? 0),
          `Pendientes: ${loading ? '—' : counts.partners_pending ?? 0} · En revisión: ${loading ? '—' : counts.partners_reviewing ?? 0}`,
          'partners',
          adminMenuBadgeCount(menuBadges, 'partner_registro')
        )}
        {card(
          'NOTICIAS (BORRADORES)',
          counts.noticias_borradores,
          'Pendientes de publicar en la web',
          'noticias',
          adminMenuBadgeCount(menuBadges, 'noticias')
        )}
        {card(
          'COMUNIDAD',
          adminMenuBadgeCount(menuBadges, 'comunidad'),
          'Moderación y solicitudes de portada',
          'comunidad',
          adminMenuBadgeCount(menuBadges, 'comunidad')
        )}
        {card(
          'PUBLICIDAD',
          counts.ads_activos,
          'Borradores / pendientes de revisión en menú lateral',
          'publicidad',
          adminMenuBadgeCount(menuBadges, 'publicidad')
        )}
        {card(
          'HILOS PARTNER SIN LEER',
          counts.unread_partner_threads,
          'Soporte Partner (exacto por último mensaje)',
          'admin_mensajes',
          adminMenuBadgeCount(menuBadges, 'admin_mensajes')
        )}
        {card(
          'REPORTES',
          adminMenuBadgeCount(menuBadges, 'reportes'),
          'Usuarios, mensajes y errores app',
          'reportes',
          adminMenuBadgeCount(menuBadges, 'reportes')
        )}
      </div>
      <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <BtnGold type="button" style={{ width: 'auto', minWidth: 160 }} onClick={load} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar métricas'}
        </BtnGold>
        <button
          type="button"
          onClick={loadActivity}
          disabled={activityLoading}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '0.5px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)',
            color: 'rgba(255,255,255,0.75)',
            fontWeight: 650,
            fontSize: 12,
            cursor: activityLoading ? 'wait' : 'pointer',
          }}
        >
          {activityLoading ? 'Cargando log…' : 'Recargar log'}
        </button>
        <button
          type="button"
          onClick={() => onGo?.('auditoria')}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '0.5px solid rgba(200,168,75,0.28)',
            background: 'rgba(200,168,75,0.08)',
            color: C.gold,
            fontWeight: 700,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Auditoría completa
        </button>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          “Hilos sin leer” cuenta conversaciones con mensajes nuevos del Partner desde tu último leído.
        </div>
      </div>

      <DCard style={{ marginTop: 16, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 12 }}>Últimas acciones</div>
        {activityLoading ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Cargando…</div>
        ) : !activity.length ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Aún no hay acciones registradas.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activity.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 140, flexShrink: 0, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                  {a.created_at ? new Date(a.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#fff', fontWeight: 650, lineHeight: 1.35 }}>
                    {a.summary || a.action}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                    {(a.actor_label ? `${a.actor_label} · ` : '')}
                    {a.entity_type ? `${a.entity_type}${a.entity_id ? ` · ${String(a.entity_id).slice(0, 8)}…` : ''}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DCard>
    </div>
  );
}

function formatRoles(rows) {
  if (!rows?.length) return '—';
  const parts = rows
    .filter(r => r.is_active !== false)
    .map(r => ROLE_LABELS[r.role] || r.role);
  return parts.length ? [...new Set(parts)].join(' · ') : '—';
}

function Placeholder() {
  return (
    <DCard style={{ padding: 28, textAlign: 'center', background: 'rgba(200,168,75,0.04)', borderColor: 'rgba(200,168,75,0.15)' }}>
      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }}>Próximamente</div>
    </DCard>
  );
}

function PartnersAdmin({ user, onError, onBadgesRefresh, menuBadges }) {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [pendingRoleRows, setPendingRoleRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [busyRoleId, setBusyRoleId] = useState(null);
  const [busyTicketPartnerId, setBusyTicketPartnerId] = useState(null);
  const [ticketSalesGlobal, setTicketSalesGlobal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const showSeguimiento = tab === 'pending' || tab === 'reviewing';
  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.PARTNER,
    rows: showSeguimiento ? rows : [],
    onBadgesRefresh,
    onError,
  });
  const partnerReviewingBadgeCount = Math.max(
    0,
    adminMenuBadgeCount(menuBadges, 'partner_registro') - adminMenuBadgeCount(menuBadges, 'partners')
  );

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    if (tab === 'roles_pending') {
      const { data, error } = await supabase
        .from('partner_roles')
        .select(
          'id, role, role_status, role_data, created_at, partner_id, partners ( code, company_name, contact_email )'
        )
        .eq('role_status', 'pending_approval')
        .order('created_at', { ascending: false });
      setLoading(false);
      if (error) {
        onError(error.message || 'No se pudieron cargar las solicitudes de rol');
        setPendingRoleRows([]);
        return;
      }
      setPendingRoleRows(data || []);
      return;
    }
    const partnerFields =
      tab === 'approved'
        ? 'id, code, company_name, nif, contact_email, contact_phone, sector, verification_status, profile_id, created_at, ticket_sales_enabled, ticket_sales_subscription_active, partner_roles(role, is_active, role_status)'
        : 'id, code, company_name, nif, contact_email, contact_phone, sector, verification_status, profile_id, created_at, partner_roles(role, is_active)';
    const { data, error } = await supabase
      .from('partners')
      .select(partnerFields)
      .eq('verification_status', tab)
      .order('created_at', { ascending: false });
    setLoading(false);
    if (error) {
      onError(error.message || 'No se pudieron cargar los partners');
      setRows([]);
      return;
    }
    setRows(data || []);
  }, [tab, onError]);

  const approvePartnerRole = async pr => {
    setBusyRoleId(pr.id);
    onError('');
    try {
      const { error } = await supabase
        .from('partner_roles')
        .update({ role_status: 'active', is_active: true })
        .eq('id', pr.id);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'partner_role_approved',
        entity_type: 'partner_role',
        entity_id: pr.id,
        summary: `Rol partner aprobado: ${ROLE_LABELS?.[pr.role] || pr.role}`,
        metadata: { partner_id: pr.partner_id ?? null, role: pr.role },
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo aprobar el rol');
    } finally {
      setBusyRoleId(null);
    }
  };

  const rejectPartnerRole = async pr => {
    setBusyRoleId(pr.id);
    onError('');
    try {
      const { error } = await supabase
        .from('partner_roles')
        .update({ role_status: 'rejected', is_active: false })
        .eq('id', pr.id);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'partner_role_rejected',
        entity_type: 'partner_role',
        entity_id: pr.id,
        summary: `Rol partner rechazado: ${ROLE_LABELS?.[pr.role] || pr.role}`,
        metadata: { partner_id: pr.partner_id ?? null, role: pr.role },
      });
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo rechazar el rol');
    } finally {
      setBusyRoleId(null);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('app_config').select('value').eq('key', 'partner_ticket_sales').maybeSingle();
      if (!cancelled) setTicketSalesGlobal(data?.value?.enabled === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const setPartnerTicketFlag = async (partnerId, field, value) => {
    setBusyTicketPartnerId(partnerId);
    onError('');
    try {
      const { error } = await supabase.from('partners').update({ [field]: !!value }).eq('id', partnerId);
      if (error) throw error;
      await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        action: 'partner_ticket_sales_flag',
        entity_type: 'partner',
        entity_id: partnerId,
        summary: `Entradas partner: ${field}=${value ? 'true' : 'false'}`,
        metadata: { field, value: !!value },
      });
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar venta de entradas');
    } finally {
      setBusyTicketPartnerId(null);
    }
  };

  const doAction = async (p, action) => {
    const partnerId = p?.id;
    const profileId = p?.profile_id;
    setBusyId(partnerId);
    onError('');
    try {
      if (action === 'reviewing') {
        const { data, error } = await supabase.functions.invoke('admin-actions', {
          body: { action: 'partner_set_status', payload: { partner_id: partnerId, status: 'reviewing', profile_id: profileId ?? null } },
        });
        if (error || !data?.ok) throw error || new Error('No se pudo actualizar');
      } else if (action === 'approved') {
        const { data, error } = await supabase.functions.invoke('admin-actions', {
          body: { action: 'partner_set_status', payload: { partner_id: partnerId, status: 'approved', profile_id: profileId ?? null } },
        });
        if (error || !data?.ok) throw error || new Error('No se pudo aprobar');
        const { error: nfErr } = await supabase.functions.invoke('partner-notification', {
          body: {
            type: 'approved',
            id: p.id,
            code: p.code,
            company_name: p.company_name,
            contact_email: p.contact_email,
          },
        });
        if (nfErr) console.error('partner-notification approved:', nfErr);
      }
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'Error al actualizar');
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (!reason) {
      onError('Indica un motivo de rechazo');
      return;
    }
    const snap = { ...rejectTarget };
    setBusyId(rejectTarget.id);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'partner_set_status', payload: { partner_id: snap.id, status: 'rejected', reason } },
      });
      if (error || !data?.ok) throw error || new Error('No se pudo rechazar');
      setRejectTarget(null);
      setRejectReason('');
      const { error: nfErr } = await supabase.functions.invoke('partner-notification', {
        body: {
          type: 'rejected',
          id: snap.id,
          code: snap.code,
          company_name: snap.company_name,
          contact_email: snap.contact_email,
          rejection_reason: reason,
        },
      });
      if (nfErr) console.error('partner-notification rejected:', nfErr);
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'Error al rechazar');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {PARTNER_TABS.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: `0.5px solid ${tab === t.id ? C.gold : 'rgba(255,255,255,0.12)'}`,
              background: tab === t.id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
              color: tab === t.id ? C.gold : 'rgba(255,255,255,0.65)',
              fontSize: 12,
              fontWeight: tab === t.id ? 700 : 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {t.id === 'pending' ? (
              <AdminSeguimientoTabLabel label={t.label} unreadCount={adminMenuBadgeCount(menuBadges, 'partners')} />
            ) : t.id === 'reviewing' ? (
              <AdminSeguimientoTabLabel label={t.label} unreadCount={partnerReviewingBadgeCount} />
            ) : t.id === 'roles_pending' ? (
              <AdminSeguimientoTabLabel
                label={t.label}
                unreadCount={adminMenuBadgeCount(menuBadges, 'partner_roles_pending')}
              />
            ) : (
              t.label
            )}
          </button>
        ))}
        {showSeguimiento ? (
          <AdminSeguimientoMarcarTodas
            unreadCount={adminMenuBadgeCount(menuBadges, 'partner_registro')}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
        ) : null}
      </div>

      {tab !== 'roles_pending' && adminMenuBadgeCount(menuBadges, 'partner_roles_pending') > 0 ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 10,
            border: '0.5px solid rgba(200,168,75,0.35)',
            background: 'rgba(200,168,75,0.08)',
            fontSize: 12,
            color: 'rgba(255,255,255,0.75)',
            lineHeight: 1.45,
          }}
        >
          Hay {adminMenuBadgeCount(menuBadges, 'partner_roles_pending')} solicitud
          {adminMenuBadgeCount(menuBadges, 'partner_roles_pending') === 1 ? '' : 'es'} de cambio de rol en el panel Partner.{' '}
          <button
            type="button"
            onClick={() => setTab('roles_pending')}
            style={{
              margin: 0,
              padding: 0,
              border: 'none',
              background: 'none',
              color: C.gold,
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 'inherit',
            }}
          >
            Ver en «Roles pendientes»
          </button>
        </div>
      ) : null}

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando…</div>
      ) : tab === 'roles_pending' ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 720 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '8px 6px' }}>Partner</th>
                <th style={{ padding: '8px 6px' }}>Código</th>
                <th style={{ padding: '8px 6px' }}>Email</th>
                <th style={{ padding: '8px 6px' }}>Rol solicitado</th>
                <th style={{ padding: '8px 6px' }}>Horario</th>
                <th style={{ padding: '8px 6px', minWidth: 200 }}>Motivo (partner)</th>
                <th style={{ padding: '8px 6px' }}>Fecha</th>
                <th style={{ padding: '8px 6px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pendingRoleRows.map(pr => {
                const pt = pr.partners;
                const horarioTitulo = roleNeedsPartnerSchedule(pr.role)
                  ? PARTNER_SCHEDULE_OPTIONS.find(
                      o => o.value === normalizePartnerScheduleValue(pr.role_data?.schedule_type)
                    )?.title ?? '—'
                  : '—';
                return (
                  <tr key={pr.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                    <td style={{ padding: '10px 6px' }}>{pt?.company_name || '—'}</td>
                    <td style={{ padding: '10px 6px', color: C.gold, fontWeight: 600 }}>{pt?.code || '—'}</td>
                    <td style={{ padding: '10px 6px', wordBreak: 'break-all' }}>{pt?.contact_email || '—'}</td>
                    <td style={{ padding: '10px 6px' }}>{ROLE_LABELS[pr.role] || pr.role}</td>
                    <td style={{ padding: '10px 6px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.7)' }}>{horarioTitulo}</td>
                    <td style={{ padding: '10px 6px', maxWidth: 320, fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {pr.role_data?.partner_request_reason?.trim() || '—'}
                    </td>
                    <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                      {pr.created_at ? new Date(pr.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 6px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <button
                          type="button"
                          disabled={busyRoleId === pr.id}
                          onClick={() => approvePartnerRole(pr)}
                          style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                        >
                          Aprobar
                        </button>
                        <button
                          type="button"
                          disabled={busyRoleId === pr.id}
                          onClick={() => rejectPartnerRole(pr)}
                          style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                        >
                          Rechazar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!pendingRoleRows.length ? (
            <div style={{ marginTop: 12, color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>No hay solicitudes de rol pendientes.</div>
          ) : null}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {tab === 'approved' && !ticketSalesGlobal ? (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 10,
                border: '0.5px solid rgba(226,75,74,0.35)',
                background: 'rgba(226,75,74,0.08)',
                fontSize: 12,
                color: 'rgba(255,255,255,0.75)',
                lineHeight: 1.45,
              }}
            >
              La venta de entradas está <strong>desactivada globalmente</strong>. Actívala en Configuración → «Venta de entradas (partners)» antes de habilitar partners.
            </div>
          ) : null}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: tab === 'approved' ? 1080 : 900 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {showSeguimiento ? <th style={{ padding: '8px 6px', width: 72 }}>Leída</th> : null}
                <th style={{ padding: '8px 6px' }}>Código</th>
                <th style={{ padding: '8px 6px' }}>Nombre</th>
                <th style={{ padding: '8px 6px' }}>NIF</th>
                <th style={{ padding: '8px 6px' }}>Email</th>
                <th style={{ padding: '8px 6px' }}>Teléfono</th>
                <th style={{ padding: '8px 6px' }}>Sector</th>
                <th style={{ padding: '8px 6px' }}>Roles</th>
                {tab === 'approved' ? (
                  <>
                    <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>Suscripción entradas</th>
                    <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>Venta entradas</th>
                  </>
                ) : null}
                <th style={{ padding: '8px 6px' }}>Fecha</th>
                <th style={{ padding: '8px 6px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const track = showSeguimiento;
                const sinLeer = track && seguimiento.isUnread(p.id);
                return (
                <tr key={p.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                  {showSeguimiento ? (
                    <td style={{ padding: '10px 6px', verticalAlign: 'top' }}>
                      <AdminLeidaCell
                        leida={seguimiento.readIds.has(p.id)}
                        busy={seguimiento.readBusyId === p.id}
                        onChange={checked => seguimiento.setReadState(p.id, checked)}
                      />
                    </td>
                  ) : null}
                  <td style={{ padding: '10px 6px', color: C.gold, fontWeight: 600 }}>{p.code || '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{p.company_name || '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{p.nif || '—'}</td>
                  <td style={{ padding: '10px 6px', wordBreak: 'break-all' }}>{p.contact_email || '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{p.contact_phone || '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{SECTOR_LABELS[p.sector] || p.sector || '—'}</td>
                  <td style={{ padding: '10px 6px', maxWidth: 180 }}>{formatRoles(p.partner_roles)}</td>
                  {tab === 'approved' ? (
                    <>
                      <td style={{ padding: '10px 6px', verticalAlign: 'middle' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                          <input
                            type="checkbox"
                            checked={!!p.ticket_sales_subscription_active}
                            disabled={busyTicketPartnerId === p.id}
                            onChange={e => setPartnerTicketFlag(p.id, 'ticket_sales_subscription_active', e.target.checked)}
                          />
                          Activa
                        </label>
                      </td>
                      <td style={{ padding: '10px 6px', verticalAlign: 'middle' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                          <input
                            type="checkbox"
                            checked={!!p.ticket_sales_enabled}
                            disabled={busyTicketPartnerId === p.id || !ticketSalesGlobal}
                            onChange={e => setPartnerTicketFlag(p.id, 'ticket_sales_enabled', e.target.checked)}
                          />
                          Habilitada
                        </label>
                      </td>
                    </>
                  ) : null}
                  <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                    {p.created_at ? new Date(p.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 6px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {p.verification_status === 'pending' ? (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => doAction(p, 'reviewing')}
                          style={btnMini}
                        >
                          Revisar
                        </button>
                      ) : null}
                      {p.verification_status === 'pending' || p.verification_status === 'reviewing' ? (
                        <>
                          <button
                            type="button"
                            disabled={busyId === p.id}
                            onClick={() => doAction(p, 'approved')}
                            style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                          >
                            Aprobar
                          </button>
                          <button
                            type="button"
                            disabled={busyId === p.id}
                            onClick={() => {
                              setRejectTarget({
                                id: p.id,
                                code: p.code,
                                company_name: p.company_name,
                                contact_email: p.contact_email,
                              });
                              setRejectReason('');
                            }}
                            style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                          >
                            Rechazar
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
          {!rows.length ? <div style={{ marginTop: 12, color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>No hay registros en esta pestaña.</div> : null}
        </div>
      )}

      {rejectTarget ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <DCard style={{ maxWidth: 420, width: '100%', padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 10 }}>Motivo del rechazo</div>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Describe el motivo…"
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 13,
                marginBottom: 12,
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={{ background: 'transparent', color: 'rgba(255,255,255,0.55)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '10px 18px', fontWeight: 500, fontSize: 12, cursor: 'pointer', width: 'auto' }}
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason('');
                }}
              >
                Cancelar
              </button>
              <BtnGold type="button" style={{ width: 'auto', minWidth: 140 }} onClick={submitReject} disabled={busyId !== null}>
                {busyId ? 'Guardando…' : 'Guardar rechazo'}
              </BtnGold>
            </div>
          </DCard>
        </div>
      ) : null}
    </div>
  );
}

const MAINT_TEXTAREA = {
  width: '100%',
  padding: 10,
  borderRadius: 10,
  border: '0.5px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  fontSize: 13,
  fontFamily: 'inherit',
  resize: 'vertical',
};

function truncMaintMotivo(s, max = 40) {
  const t = String(s || '').trim();
  if (t.length <= max) return t || '—';
  return `${t.slice(0, max)}…`;
}

function MaintenanceHistorialPanel({ onError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeccion, setFilterSeccion] = useState('');
  const [filterAccion, setFilterAccion] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const loadLog = useCallback(async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('maintenance_log')
        .select('id, admin_id, accion, seccion, mensaje_usuarios, motivo_interno, config_snapshot, created_at, profiles:admin_id ( username )')
        .order('created_at', { ascending: false })
        .limit(50);
      if (filterSeccion) q = q.eq('seccion', filterSeccion);
      if (filterAccion) q = q.eq('accion', filterAccion);
      const { data, error } = await q;
      if (error) throw error;
      setRows(data ?? []);
    } catch (e) {
      onErrorRef.current?.(e?.message || 'No se pudo cargar el historial');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterSeccion, filterAccion]);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  const accionLabel = key => MAINTENANCE_LOG_ACCIONES.find(a => a.key === key)?.label || key || '—';

  const filterSelectStyle = {
    padding: '8px 10px',
    borderRadius: 10,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 12,
    fontFamily: 'inherit',
    minWidth: 160,
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <select value={filterSeccion} onChange={e => setFilterSeccion(e.target.value)} style={filterSelectStyle}>
          {MAINTENANCE_LOG_SECTIONS.map(o => (
            <option key={o.key || 'all'} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <select value={filterAccion} onChange={e => setFilterAccion(e.target.value)} style={filterSelectStyle}>
          {MAINTENANCE_LOG_ACCIONES.map(o => (
            <option key={o.key || 'all-acc'} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => loadLog()}
          style={{
            padding: '8px 14px',
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.14)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.82)',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {loading ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {loading && !rows.length ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando historial…</div>
      ) : !rows.length ? (
        <DCard style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
          Sin eventos de mantenimiento registrados
        </DCard>
      ) : (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '0.5px solid rgba(255,255,255,0.1)', textAlign: 'left' }}>
                  {['Fecha/hora', 'Acción', 'Sección', 'Motivo interno', 'Admin'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const open = expandedId === row.id;
                  const adminName = row.profiles?.username || '—';
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        onClick={() => setExpandedId(open ? null : row.id)}
                        style={{
                          borderBottom: '0.5px solid rgba(255,255,255,0.06)',
                          cursor: 'pointer',
                          background: open ? 'rgba(200,168,75,0.06)' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }}>
                          {row.created_at ? new Date(row.created_at).toLocaleString('es-ES') : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', color: C.gold }}>{accionLabel(row.accion)}</td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>
                          {row.seccion ? maintenanceSectionLabel(row.seccion) : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.65)', maxWidth: 200 }}>
                          {truncMaintMotivo(row.motivo_interno)}
                        </td>
                        <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{adminName}</td>
                      </tr>
                      {open ? (
                        <tr>
                          <td colSpan={5} style={{ padding: '12px 14px 16px', background: 'rgba(0,0,0,0.2)' }}>
                            {row.motivo_interno ? (
                              <div style={{ marginBottom: 10, fontSize: 12, color: 'rgba(255,255,255,0.88)' }}>
                                <strong style={{ color: C.gold }}>Motivo interno</strong>
                                <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{row.motivo_interno}</div>
                              </div>
                            ) : null}
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>config_snapshot</div>
                            <pre
                              style={{
                                margin: 0,
                                padding: 12,
                                borderRadius: 8,
                                background: 'rgba(0,0,0,0.35)',
                                border: '0.5px solid rgba(255,255,255,0.08)',
                                fontSize: 10,
                                color: 'rgba(255,255,255,0.75)',
                                overflow: 'auto',
                                maxHeight: 280,
                              }}
                            >
                              {JSON.stringify(row.config_snapshot ?? {}, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DCard>
      )}
    </div>
  );
}

function MaintenanceAdmin({ onError, onSaved }) {
  const [panelTab, setPanelTab] = useState('config');
  const [cfg, setCfg] = useState(() => parseMaintenanceConfig(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState('');
  const [histRefreshKey, setHistRefreshKey] = useState(0);
  const onErrorRef = useRef(onError);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    onErrorRef.current = onError;
    onSavedRef.current = onSaved;
  }, [onError, onSaved]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('app_config').select('value').eq('key', 'maintenance').maybeSingle();
      if (error) throw error;
      setCfg(parseMaintenanceConfig(data?.value));
    } catch (e) {
      onErrorRef.current?.(e?.message || 'No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setSaveOk('');
    try {
      const value = buildMaintenancePayload(cfg);
      const { data, error } = await supabase
        .from('app_config')
        .upsert({ key: 'maintenance', value }, { onConflict: 'key' })
        .select('value')
        .maybeSingle();
      if (error) throw error;
      const saved = parseMaintenanceConfig(data?.value ?? value);
      setCfg(saved);
      const okMsg = 'Mantenimiento guardado. Las secciones marcadas quedan bloqueadas para los usuarios.';
      setSaveOk(okMsg);
      onSavedRef.current?.(okMsg);
      setHistRefreshKey(k => k + 1);
    } catch (e) {
      onErrorRef.current?.(e?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const tabBtn = id => ({
    padding: '8px 14px',
    borderRadius: 10,
    border: `0.5px solid ${panelTab === id ? C.gold : 'rgba(255,255,255,0.12)'}`,
    background: panelTab === id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
    color: panelTab === id ? C.gold : 'rgba(255,255,255,0.65)',
    fontSize: 12,
    fontWeight: panelTab === id ? 700 : 500,
    cursor: 'pointer',
  });

  if (loading && panelTab === 'config') {
    return <div style={{ color: 'rgba(255,255,255,0.45)' }}>Cargando configuración…</div>;
  }

  return (
    <div style={{ maxWidth: panelTab === 'historial' ? 960 : 560 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" style={tabBtn('config')} onClick={() => setPanelTab('config')}>
          Configuración
        </button>
        <button type="button" style={tabBtn('historial')} onClick={() => setPanelTab('historial')}>
          Historial
        </button>
      </div>
      {panelTab === 'historial' ? (
        <MaintenanceHistorialPanel key={histRefreshKey} onError={onError} />
      ) : (
        <>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 14, lineHeight: 1.5 }}>
        Activa el interruptor global y marca las secciones afectadas. Si marcas una sección sin activar el global, al guardar se activará automáticamente.
      </p>
      <DCard style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 10 }}>Modo mantenimiento global</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
          <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg(prev => ({ ...prev, enabled: e.target.checked }))} style={{ accentColor: C.gold }} />
          Activado
        </label>
      </DCard>

      <DCard style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12 }}>Secciones</div>
        {SECTION_MAINT_KEYS.map(({ key, label }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.78)' }}>
            <input
              type="checkbox"
              checked={!!cfg.sections[key]}
              onChange={e => setCfg(prev => ({
                ...prev,
                sections: { ...prev.sections, [key]: e.target.checked },
              }))}
              style={{ accentColor: C.gold }}
            />
            {label}
          </label>
        ))}
      </DCard>

      <DCard style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Mensaje para el usuario</div>
        <textarea
          value={cfg.message}
          onChange={e => setCfg(prev => ({ ...prev, message: e.target.value }))}
          rows={4}
          placeholder="Texto opcional cuando el mantenimiento afecte a una zona…"
          style={MAINT_TEXTAREA}
        />
      </DCard>

      <DCard style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Fin estimado (opcional)</div>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', marginBottom: 8, lineHeight: 1.45 }}>
          Se muestra a los usuarios como «Volvemos aproximadamente a las HH:MM».
        </p>
        <input
          type="datetime-local"
          value={maintenanceFinEstimadoToDatetimeLocal(cfg.fin_estimado)}
          onChange={e => setCfg(prev => ({
            ...prev,
            fin_estimado: maintenanceDatetimeLocalToIso(e.target.value),
          }))}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.06)',
            color: '#fff',
            fontSize: 13,
            fontFamily: 'inherit',
            colorScheme: 'dark',
          }}
        />
        {cfg.fin_estimado ? (
          <button
            type="button"
            onClick={() => setCfg(prev => ({ ...prev, fin_estimado: null }))}
            style={{
              marginTop: 8,
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.45)',
              fontSize: 11,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Quitar fin estimado
          </button>
        ) : null}
      </DCard>

      <DCard style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Motivo interno (solo visible en admin)
        </div>
        <textarea
          value={cfg.motivo_interno}
          onChange={e => setCfg(prev => ({ ...prev, motivo_interno: e.target.value }))}
          rows={2}
          placeholder="Ej: Migración tabla listings, actualización de Edge Functions…"
          style={MAINT_TEXTAREA}
        />
      </DCard>

      <BtnGold type="button" style={{ width: 'auto', maxWidth: 220 }} onClick={save} disabled={saving}>
        {saving ? 'Guardando…' : 'Guardar'}
      </BtnGold>
      {saveOk ? (
        <p style={{ marginTop: 12, fontSize: 12, color: C.green, lineHeight: 1.5 }}>{saveOk}</p>
      ) : null}
        </>
      )}
    </div>
  );
}

function adsToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function adsFromDatetimeLocalValue(s) {
  if (!s || !String(s).trim()) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function AdminAdsDrafts({ user, onError, onSuccess, onBadgesRefresh, menuBadges }) {
  const [tab, setTab] = useState('borradores');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [dateDraft, setDateDraft] = useState({});
  /** null = cerrado; 'new' = alta; UUID = edición */
  const [editorAdId, setEditorAdId] = useState(null);
  const [editorInitialSlot, setEditorInitialSlot] = useState('hero_marketplace');
  const [slotFilter, setSlotFilter] = useState('');
  const [slotsOverviewKey, setSlotsOverviewKey] = useState(0);

  const seguimientoTrack = tab === 'borradores' || tab === 'revision';
  const seguimientoRows = useMemo(() => {
    if (tab === 'borradores') return rows;
    if (tab === 'revision') return rows.filter(r => r.status === 'pendiente_revision');
    return [];
  }, [tab, rows]);

  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.PUBLICIDAD,
    rows: seguimientoRows,
    onBadgesRefresh,
    onError,
  });

  const load = useCallback(async () => {
    setLoading(true);
    onError('');
    let q = supabase
      .from('ads')
      .select(
        'id, campaign_name, slot, status, starts_at, ends_at, created_at, updated_at, impressions, clicks, media_url, sort_order, advertiser_id, advertisers ( code, company_name, contact_email )'
      );
    if (tab === 'borradores') q = q.eq('status', 'borrador');
    else if (tab === 'activas') q = q.eq('status', 'activo');
    else if (tab === 'revision') q = q.in('status', ['pendiente_revision', 'aprobado']);
    else if (tab === 'archivo') q = q.in('status', ['finalizado', 'rechazado']);
    if (slotFilter) q = q.eq('slot', slotFilter);
    q =
      tab === 'activas'
        ? q.order('slot', { ascending: true }).order('sort_order', { ascending: true }).order('created_at', { ascending: true })
        : q.order(tab === 'todas' ? 'updated_at' : 'created_at', { ascending: false });
    q = q.limit(tab === 'todas' ? 120 : 100);
    const { data, error } = await q;
    setLoading(false);
    if (error) {
      onError(error.message || 'No se pudieron cargar las campañas');
      setRows([]);
      setDateDraft({});
      return;
    }
    const list = data ?? [];
    setRows(list);
    setDateDraft(
      Object.fromEntries(list.map(r => [r.id, { starts: adsToDatetimeLocalValue(r.starts_at), ends: adsToDatetimeLocalValue(r.ends_at) }]))
    );
  }, [onError, tab, slotFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const bumpSlotsOverview = () => setSlotsOverviewKey(k => k + 1);

  const openNewInSlot = slotValue => {
    setEditorInitialSlot(slotValue || 'hero_marketplace');
    setEditorAdId('new');
  };

  const removeAd = async row => {
    const st = String(row.status || '').toLowerCase();
    const isDraftLike = st === 'borrador' || st === 'rechazado' || st === 'finalizado';
    const msg = isDraftLike
      ? '¿Eliminar esta campaña de forma permanente?'
      : '¿Quitar del sitio? La campaña pasará a estado finalizado.';
    if (!window.confirm(msg)) return;
    setBusyId(row.id);
    onError('');
    try {
      if (isDraftLike) {
        await deleteAdminAd(row.id);
        await load();
        bumpSlotsOverview();
        onBadgesRefresh?.();
      } else {
        await setAdStatus(row, 'finalizado');
        bumpSlotsOverview();
      }
    } catch (e) {
      onError(e?.message || 'No se pudo quitar la campaña');
    } finally {
      setBusyId(null);
    }
  };

  const duplicateAd = async row => {
    setBusyId(row.id);
    onError('');
    try {
      const newId = await duplicateAdminAd(row.id);
      await load();
      bumpSlotsOverview();
      onBadgesRefresh?.();
      setEditorInitialSlot(row.slot || 'hero_marketplace');
      setEditorAdId(newId);
    } catch (e) {
      onError(e?.message || 'No se pudo duplicar');
    } finally {
      setBusyId(null);
    }
  };

  const nudgeSortOrder = async (row, delta) => {
    setBusyId(row.id);
    onError('');
    try {
      const next = (row.sort_order ?? 0) + delta;
      await updateAdminAdSortOrder(row.id, Math.max(0, next));
      await load();
      bumpSlotsOverview();
    } catch (e) {
      onError(e?.message || 'No se pudo cambiar el orden');
    } finally {
      setBusyId(null);
    }
  };

  const setAdStatus = async (row, status) => {
    setBusyId(row.id);
    onError('');
    try {
      const patch = { status };
      if (status === 'activo') {
        const now = new Date();
        const starts = new Date(row.starts_at);
        const ends = new Date(row.ends_at);
        if (!Number.isNaN(starts.getTime()) && starts > now) {
          patch.starts_at = now.toISOString();
        }
        if (!Number.isNaN(ends.getTime()) && ends <= now) {
          const end = new Date(now);
          end.setDate(end.getDate() + 30);
          patch.ends_at = end.toISOString();
        }
      }
      const { error } = await supabase.from('ads').update(patch).eq('id', row.id);
      if (error) throw error;
      await load();
      bumpSlotsOverview();
      onBadgesRefresh?.();
      if (status === 'activo' && patch.starts_at) {
        onSuccess?.('Activada. La fecha de inicio se ajustó a «ahora» para que aparezca en la web de inmediato.');
      }
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar la campaña');
    } finally {
      setBusyId(null);
    }
  };

  const updateDateField = (id, field, value) => {
    setDateDraft(prev => ({
      ...prev,
      [id]: { ...(prev[id] || { starts: '', ends: '' }), [field]: value },
    }));
  };

  const saveAdDates = async row => {
    const dr = dateDraft[row.id];
    if (!dr) return;
    const starts = adsFromDatetimeLocalValue(dr.starts);
    const ends = adsFromDatetimeLocalValue(dr.ends);
    if (!starts || !ends) {
      onError('Indica inicio y fin válidos');
      return;
    }
    if (new Date(ends) <= new Date(starts)) {
      onError('La fecha de fin debe ser posterior al inicio');
      return;
    }
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase.from('ads').update({ starts_at: starts, ends_at: ends }).eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudieron guardar las fechas');
    } finally {
      setBusyId(null);
    }
  };

  const tabLabels = {
    borradores: 'Borradores',
    activas: 'Activas',
    revision: 'Revisión / aprobadas',
    archivo: 'Archivo',
    todas: 'Todas (recientes)',
  };

  const pills = ['borradores', 'activas', 'revision', 'archivo', 'todas'];

  return (
    <div>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 }}>
        Gestiona los <strong style={{ color: C.gold, fontWeight: 700 }}>slides</strong> de cada pantalla (hero, laterales, patrocinado):
        añadir, editar, quitar, duplicar y ordenar el carrusel. En el <strong>hero del Mercado</strong>, las campañas visibles se muestran
        <em> primero</em> y después el vídeo y banners por defecto. Comprueba «En web ahora» en la tabla (status activo + fechas vigentes + archivo subido).
      </p>
      <AdminAdSlotsOverview
        key={slotsOverviewKey}
        slotFilter={slotFilter}
        onSlotFilter={setSlotFilter}
        onAddInSlot={openNewInSlot}
        onPromptCopied={msg => {
          onError('');
          onSuccess?.(msg || 'Copiado al portapapeles');
        }}
      />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <BtnGold
          type="button"
          style={{ width: 'auto', minWidth: 180 }}
          onClick={() => openNewInSlot(slotFilter || 'hero_marketplace')}
          disabled={editorAdId === 'new'}
        >
          Nueva campaña
        </BtnGold>
        {slotFilter ? (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
            Filtrando: <span style={{ color: C.gold }}>{getAdSlotLabel(slotFilter)}</span>
          </span>
        ) : null}
      </div>
      {editorAdId ? (
        <AdminAdCampaignForm
          adId={editorAdId === 'new' ? null : editorAdId}
          initialSlot={editorInitialSlot}
          onClose={() => setEditorAdId(null)}
          onSaved={() => {
            load();
            bumpSlotsOverview();
            onBadgesRefresh?.();
          }}
          onError={onError}
        />
      ) : null}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {pills.map(id => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: `0.5px solid ${tab === id ? C.gold : 'rgba(255,255,255,0.12)'}`,
              background: tab === id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
              color: tab === id ? C.gold : 'rgba(255,255,255,0.65)',
              fontSize: 11,
              fontWeight: tab === id ? 700 : 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {id === 'borradores' || id === 'revision' ? (
              <AdminSeguimientoTabLabel label={tabLabels[id]} unreadCount={adminMenuBadgeCount(menuBadges, 'publicidad')} />
            ) : (
              tabLabels[id]
            )}
          </button>
        ))}
        {seguimientoTrack ? (
          <AdminSeguimientoMarcarTodas
            unreadCount={adminMenuBadgeCount(menuBadges, 'publicidad')}
            markingAllRead={seguimiento.markingAllRead}
            onMarcarTodas={seguimiento.marcarTodasLeidas}
          />
        ) : null}
        <button type="button" onClick={load} style={{ ...btnMini, marginLeft: 'auto' }} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </div>
      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1080 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {seguimientoTrack ? <th style={{ padding: '8px 6px', width: 72 }}>Leída</th> : null}
                <th style={{ padding: '8px 6px', width: 48 }} />
                <th style={{ padding: '8px 6px' }}>Campaña</th>
                <th style={{ padding: '8px 6px' }}>Anunciante</th>
                <th style={{ padding: '8px 6px' }}>Slot / orden / estado</th>
                <th style={{ padding: '8px 6px' }}>Impres. / clics</th>
                <th style={{ padding: '8px 6px', minWidth: 220 }}>Fechas (editar)</th>
                <th style={{ padding: '8px 6px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(a => {
                const adv = a.advertisers;
                const dr = dateDraft[a.id] || { starts: '', ends: '' };
                const readOnlyDates = tab === 'archivo';
                const track =
                  seguimientoTrack && (tab === 'borradores' || (tab === 'revision' && a.status === 'pendiente_revision'));
                const sinLeer = track && seguimiento.isUnread(a.id);
                return (
                  <tr key={a.id} style={adminSeguimientoRowStyle(sinLeer, { borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' })}>
                    {seguimientoTrack ? (
                      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                        {track ? (
                          <AdminLeidaCell
                            leida={seguimiento.readIds.has(a.id)}
                            busy={seguimiento.readBusyId === a.id}
                            onChange={checked => seguimiento.setReadState(a.id, checked)}
                          />
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                        )}
                      </td>
                    ) : null}
                    <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                      {a.media_url ? (
                        <img
                          src={a.media_url}
                          alt=""
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.12)' }}
                        />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(255,255,255,0.06)' }} />
                      )}
                    </td>
                    <td style={{ padding: '10px 6px', maxWidth: 180, verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 800 }}>{a.campaign_name}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                        {a.created_at ? new Date(a.created_at).toLocaleDateString('es-ES') : '—'}
                      </div>
                    </td>
                    <td style={{ padding: '10px 6px', verticalAlign: 'top' }}>
                      <div>{adv?.company_name || '—'}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{adv?.contact_email || '—'}</div>
                    </td>
                    <td style={{ padding: '10px 6px', verticalAlign: 'top', fontSize: 10, lineHeight: 1.4 }}>
                      <div style={{ whiteSpace: 'nowrap' }} title={a.slot}>
                        {getAdSlotLabel(a.slot)}
                      </div>
                      <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.5)' }}>Orden: {a.sort_order ?? 0}</div>
                      <div style={{ marginTop: 4, color: C.gold, fontWeight: 700 }}>{a.status}</div>
                      {a.status === 'activo' ? (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 9,
                            fontWeight: 700,
                            color: isAdVisibleOnSiteNow(a) ? C.green : 'rgba(239,159,39,0.95)',
                          }}
                        >
                          {isAdVisibleOnSiteNow(a)
                            ? 'En web ahora'
                            : !String(a.media_url || '').trim()
                              ? 'Sin archivo — no visible'
                              : 'Fuera de ventana (fechas)'}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 6px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      <div>{a.impressions ?? 0} / {a.clicks ?? 0}</div>
                    </td>
                    <td style={{ padding: '10px 6px', verticalAlign: 'top' }}>
                      {readOnlyDates ? (
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.45 }}>
                          {a.starts_at ? new Date(a.starts_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          <br />
                          → {a.ends_at ? new Date(a.ends_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <input
                            type="datetime-local"
                            value={dr.starts}
                            onChange={e => updateDateField(a.id, 'starts', e.target.value)}
                            style={{
                              padding: 6,
                              borderRadius: 8,
                              border: '0.5px solid rgba(255,255,255,0.12)',
                              background: 'rgba(255,255,255,0.06)',
                              color: '#fff',
                              fontSize: 10,
                              maxWidth: '100%',
                            }}
                          />
                          <input
                            type="datetime-local"
                            value={dr.ends}
                            onChange={e => updateDateField(a.id, 'ends', e.target.value)}
                            style={{
                              padding: 6,
                              borderRadius: 8,
                              border: '0.5px solid rgba(255,255,255,0.12)',
                              background: 'rgba(255,255,255,0.06)',
                              color: '#fff',
                              fontSize: 10,
                              maxWidth: '100%',
                            }}
                          />
                          <button type="button" style={btnMini} disabled={busyId === a.id} onClick={() => saveAdDates(a)}>
                            Guardar fechas
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 6px', verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <button
                          type="button"
                          style={btnMini}
                          disabled={busyId === a.id}
                          onClick={() => {
                            setEditorInitialSlot(a.slot || 'hero_marketplace');
                            setEditorAdId(a.id);
                          }}
                        >
                          Editar
                        </button>
                        <button type="button" style={btnMini} disabled={busyId === a.id} onClick={() => duplicateAd(a)}>
                          Duplicar
                        </button>
                        {tab === 'activas' ? (
                          <>
                            <button
                              type="button"
                              style={btnMini}
                              disabled={busyId === a.id}
                              onClick={() => nudgeSortOrder(a, -1)}
                              title="Subir en carrusel"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              style={btnMini}
                              disabled={busyId === a.id}
                              onClick={() => nudgeSortOrder(a, 1)}
                              title="Bajar en carrusel"
                            >
                              ↓
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                          disabled={busyId === a.id}
                          onClick={() => removeAd(a)}
                        >
                          Quitar
                        </button>
                        {a.status === 'borrador' ? (
                          <>
                            <button type="button" disabled={busyId === a.id} onClick={() => setAdStatus(a, 'pendiente_revision')} style={btnMini}>
                              A revisión
                            </button>
                            <button
                              type="button"
                              disabled={busyId === a.id}
                              onClick={() => setAdStatus(a, 'activo')}
                              style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                            >
                              Activar
                            </button>
                            <button
                              type="button"
                              disabled={busyId === a.id}
                              onClick={() => setAdStatus(a, 'rechazado')}
                              style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                            >
                              Rechazar
                            </button>
                          </>
                        ) : null}
                        {a.status === 'pendiente_revision' ? (
                          <>
                            <button type="button" disabled={busyId === a.id} onClick={() => setAdStatus(a, 'aprobado')} style={btnMini}>
                              Aprobar
                            </button>
                            <button
                              type="button"
                              disabled={busyId === a.id}
                              onClick={() => setAdStatus(a, 'activo')}
                              style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                            >
                              Activar
                            </button>
                            <button
                              type="button"
                              disabled={busyId === a.id}
                              onClick={() => setAdStatus(a, 'rechazado')}
                              style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                            >
                              Rechazar
                            </button>
                          </>
                        ) : null}
                        {a.status === 'aprobado' ? (
                          <button
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() => setAdStatus(a, 'activo')}
                            style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                          >
                            Activar
                          </button>
                        ) : null}
                        {a.status === 'activo' ? (
                          <button
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() => {
                              if (!window.confirm('¿Finalizar esta campaña? Pasará a estado finalizado.')) return;
                              setAdStatus(a, 'finalizado');
                            }}
                            style={{ ...btnMini, borderColor: 'rgba(239,159,39,0.55)', color: 'rgba(239,159,39,0.95)' }}
                          >
                            Finalizar
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length ? (
            <div style={{ marginTop: 12, color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>No hay campañas en esta vista.</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function AdminPartnerMessages({ user, onError, onBadgesRefresh }) {
  const [convs, setConvs] = useState([]);
  const [namesByBuyer, setNamesByBuyer] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);
  const markReadTimerRef = useRef(null);

  const loadConvs = useCallback(async () => {
    setLoading(true);
    onError('');
    const { data: rows, error } = await supabase
      .from('conversations')
      .select('id, buyer_id, seller_id, updated_at, conversation_type')
      .eq('conversation_type', 'partner_support')
      .order('updated_at', { ascending: false });
    setLoading(false);
    if (error) {
      onError(error.message || 'No se pudieron cargar las conversaciones');
      setConvs([]);
      return;
    }
    const list = rows ?? [];
    setConvs(list);
    const ids = [...new Set(list.map(c => c.buyer_id).filter(Boolean))];
    if (ids.length) {
      const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
      setNamesByBuyer(Object.fromEntries((profs ?? []).map(p => [p.id, p.username || 'Usuario'])));
    } else {
      setNamesByBuyer({});
    }
  }, [onError]);

  useEffect(() => {
    loadConvs();
  }, [loadConvs]);

  const markRead = useCallback(
    async convId => {
      if (!convId || !user?.id) return;
      try {
        await supabase
          .from('conversation_reads')
          .upsert(
            {
              conversation_id: convId,
              reader_id: user.id,
              last_read_at: new Date().toISOString(),
            },
            { onConflict: 'conversation_id,reader_id' }
          );
      } catch (_) {
        // Silencioso: no bloquea UX
      } finally {
        onBadgesRefresh?.();
      }
    },
    [user?.id, onBadgesRefresh]
  );

  const markReadSoon = useCallback(
    convId => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = setTimeout(() => markRead(convId), 250);
    },
    [markRead]
  );

  const loadThread = useCallback(async convId => {
    const { data, error } = await supabase
      .from('messages')
      .select('id, sender_id, content, created_at, moderation_status')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });
    if (error) return;
    setMessages(data ?? []);
  }, []);

  useEffect(() => {
    if (!selected?.id) {
      setMessages([]);
      return undefined;
    }
    loadThread(selected.id);
    markReadSoon(selected.id);
    const channel = supabase
      .channel(`admin-partner-msg:${selected.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${selected.id}` },
        payload => {
          const row = payload.new;
          if (!row?.id) return;
          setMessages(prev => {
            if (prev.some(m => m.id === row.id)) return prev;
            return [
              ...prev,
              {
                id: row.id,
                sender_id: row.sender_id,
                content: row.content,
                created_at: row.created_at,
                moderation_status: row.moderation_status,
              },
            ];
          });
          markReadSoon(selected.id);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [selected?.id, loadThread, markReadSoon]);

  useEffect(() => {
    return () => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    };
  }, []);

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages.length, selected?.id]);

  const send = async () => {
    const t = text.trim();
    if (!t || !selected?.id || !user?.id || sending) return;
    const mm = moderateText(t, 'chat');
    if (!mm.approved) {
      onError(mm.reason || 'No se pudo enviar');
      return;
    }
    setSending(true);
    onError('');
    const { error } = await supabase.from('messages').insert({
      conversation_id: selected.id,
      sender_id: user.id,
      content: t,
      is_system: false,
    });
    setSending(false);
    if (error) {
      onError(error.message || 'No se pudo enviar');
      return;
    }
    await supabase.from('admin_activity_log').insert({
        actor_id: user?.id ?? null,
        actor_label: user?.email ?? null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      action: 'partner_message_sent',
      entity_type: 'conversation',
      entity_id: selected.id,
      summary: `Respuesta enviada a Partner: @${namesByBuyer[selected.buyer_id] || 'Partner'}`,
      metadata: { conversation_id: selected.id, buyer_id: selected.buyer_id ?? null },
    });
    onBadgesRefresh?.();
    setText('');
    loadConvs();
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', flexWrap: 'wrap', minHeight: 420 }}>
      <DCard style={{ flex: '0 0 280px', maxWidth: '100%', padding: 12, maxHeight: 520, overflowY: 'auto', alignSelf: 'stretch' }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 10 }}>Conversaciones Partner</div>
        {loading ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Cargando…</div>
        ) : !convs.length ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay hilos partner_support.</div>
        ) : (
          convs.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelected(c)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 10px',
                marginBottom: 6,
                borderRadius: 10,
                border: `0.5px solid ${selected?.id === c.id ? C.gold : 'rgba(255,255,255,0.1)'}`,
                background: selected?.id === c.id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              <div style={{ fontWeight: 650 }}>@{namesByBuyer[c.buyer_id] || 'Partner'}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                {c.updated_at ? new Date(c.updated_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : ''}
              </div>
            </button>
          ))
        )}
      </DCard>
      <DCard style={{ flex: '1 1 360px', minWidth: 0, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
        {!selected ? (
          <div style={{ padding: 24, color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Selecciona una conversación.</div>
        ) : (
          <>
            <div style={{ padding: '12px 14px', borderBottom: '0.5px solid rgba(200,168,75,0.15)', fontSize: 13, color: C.gold }}>
              Chat con @{namesByBuyer[selected.buyer_id] || 'Partner'}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, minHeight: 260, maxHeight: 400 }}>
              {messages.map(m => {
                const mine = m.sender_id === user?.id;
                const hidden = String(m.moderation_status || 'approved') === 'hidden';
                return (
                  <div
                    key={m.id}
                    style={{
                      marginBottom: 10,
                      display: 'flex',
                      justifyContent: mine ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '85%',
                        padding: '8px 11px',
                        borderRadius: 12,
                        fontSize: 13,
                        lineHeight: 1.45,
                        background: mine ? 'rgba(200,168,75,0.22)' : 'rgba(255,255,255,0.06)',
                        color: '#eee',
                        fontStyle: hidden ? 'italic' : undefined,
                        opacity: hidden ? 0.8 : 1,
                      }}
                    >
                      {hidden ? 'Mensaje ocultado por moderación' : m.content}
                    </div>
                  </div>
                );
              })}
              <div ref={listEndRef} />
            </div>
            <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '0.5px solid rgba(200,168,75,0.15)' }}>
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Responder como equipo DreamVinyl…"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontSize: 13,
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <BtnGold type="button" style={{ width: 'auto', minWidth: 88 }} disabled={sending || !text.trim()} onClick={send}>
                Enviar
              </BtnGold>
            </div>
          </>
        )}
      </DCard>
    </div>
  );
}

export default function PageAdmin({
  onBack,
  onNavigate,
  user,
  initialActive = null,
  initialComunidadTab = null,
}) {
  const [isAdmin, setIsAdmin] = useState(null);
  const [active, setActive] = useState('dashboard');
  const [bannerError, setBannerError] = useState('');
  const [bannerSuccess, setBannerSuccess] = useState('');
  const [menuBadges, setMenuBadges] = useState(emptyAdminMenuBadges());
  const [menuBadgesLoading, setMenuBadgesLoading] = useState(false);
  /** Acordeón lateral: un grupo abierto a la vez; null = todos colapsados */
  const [menuOpenGroupId, setMenuOpenGroupId] = useState('grp_inicio');
  const [socialKitGlobalOpen, setSocialKitGlobalOpen] = useState(null);
  const [fichaLoadingId, setFichaLoadingId] = useState(null);

  const handleViewListingFicha = useCallback(
    async listingId => {
      const id = String(listingId ?? '').trim();
      if (!id || !onNavigate) return;
      setFichaLoadingId(id);
      setBannerError('');
      try {
        const { data, error } = await fetchPublicListingById(id);
        if (error || !data) {
          setBannerError(error || 'No se pudo abrir la ficha del anuncio.');
          return;
        }
        onNavigate('ficha', data);
      } finally {
        setFichaLoadingId(null);
      }
    },
    [onNavigate]
  );

  /**
   * Back UX:
   * - Si estamos dentro de una sección (p.ej. "Estadísticas"), volver debería llevar al Dashboard del admin.
   * - Si ya estamos en Dashboard, entonces sí salir del admin (onBack del parent).
   */
  const handleBack = useCallback(() => {
    if (active && active !== 'dashboard') {
      setActive('dashboard');
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
      return;
    }
    onBack?.();
  }, [active, onBack]);

  const reportAdminError = useCallback(msg => {
    if (msg) {
      setBannerSuccess('');
      setBannerError(msg);
    } else {
      setBannerError('');
    }
  }, []);

  const reportAdminSaved = useCallback(msg => {
    setBannerError('');
    setBannerSuccess(msg || '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setIsAdmin(false);
        return;
      }
      const { data } = await supabase.from('profiles').select('is_admin').eq('id', user.id).maybeSingle();
      if (!cancelled) setIsAdmin(!!data?.is_admin);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!initialActive) return;
    const ok = MENU_FLAT.some(m => m.id === initialActive);
    if (!ok) return;
    setActive(initialActive);
    const grp = MENU_GROUPS.find(g => g.items.some(i => i.id === initialActive));
    if (grp) setMenuOpenGroupId(grp.id);
  }, [initialActive]);

  const activeMenuItem = useMemo(() => MENU_FLAT.find(m => m.id === active), [active]);
  const mainTitle = activeMenuItem?.label || 'Administración';
  const mainHint = activeMenuItem?.hint || '';

  const loadMenuBadges = useCallback(async ({ showLoading = false } = {}) => {
    if (!user?.id) return;
    try {
      if (showLoading) setMenuBadgesLoading(true);
      const badges = await fetchAdminMenuBadgeCounts(user.id);
      setMenuBadges(badges);
    } catch (_) {
      // Silencioso: no bloquea la navegación del admin
    } finally {
      if (showLoading) setMenuBadgesLoading(false);
    }
  }, [user?.id]);

  const activePendingCount = useMemo(() => {
    if (active === 'dashboard') return adminMenuBadgeTotal(menuBadges);
    return adminMenuBadgeCount(menuBadges, active);
  }, [active, menuBadges]);

  useEffect(() => {
    loadMenuBadges({ showLoading: true });
  }, [loadMenuBadges]);

  useEffect(() => {
    if (active === 'maintenance') return;
    loadMenuBadges();
  }, [active, loadMenuBadges]);

  useEffect(() => {
    const g = MENU_GROUPS.find(gr => gr.items.some(i => i.id === active));
    if (g) setMenuOpenGroupId(g.id);
  }, [active]);

  if (isAdmin === null) {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={handleBack} title="Administración" mini dark user={user} onNavigate={onNavigate} onSignOut={handleBack} />
        <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.45)' }}>Comprobando acceso…</div>
      </div>
    );
  }

  if (!user?.id || !isAdmin) {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={handleBack} title="Administración" mini dark user={user} onNavigate={onNavigate} onSignOut={handleBack} />
        <div style={{ maxWidth: 420, margin: '48px auto', padding: 24, textAlign: 'center' }}>
          <DCard style={{ padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Acceso no autorizado</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>
              Esta área está reservada a administradores DreamVinyl.
            </div>
            <BtnGold type="button" style={{ width: 'auto', minWidth: 160 }} onClick={handleBack}>
              Volver
            </BtnGold>
          </DCard>
        </div>
      </div>
    );
  }

  const isPlaceholder = ![
    'dashboard',
    'partners',
    'maintenance',
    'noticias',
    'comunidad',
    'publicidad',
    'admin_mensajes',
    'usuarios',
    'grooves',
    'sorteos',
    'estadisticas',
    'listings',
    'valoraciones',
    'reportes',
    'notificaciones',
    'eventos',
    'emails',
    'directorio',
    'config',
    'partner_registro',
    'subastas',
    'transacciones',
    'auditoria',
    'redes_cola',
  ].includes(active);

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <WebTopBar onBack={handleBack} title="Administración" mini dark user={user} onNavigate={onNavigate} onSignOut={handleBack} />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <aside
          style={{
            width: 230,
            flexShrink: 0,
            borderRight: '0.5px solid rgba(200,168,75,0.18)',
            background: 'linear-gradient(180deg, #0c0c12 0%, #0a0a0f 100%)',
            padding: '16px 10px',
            overflowY: 'auto',
          }}
        >
          <div style={{ fontSize: 10, color: 'rgba(200,168,75,0.55)', letterSpacing: '0.12em', margin: '0 8px 12px', fontWeight: 700 }}>MENÚ</div>
          <button
            type="button"
            onClick={() => loadMenuBadges()}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '9px 12px',
              marginBottom: 10,
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.04)',
              color: 'rgba(255,255,255,0.75)',
              cursor: menuBadgesLoading ? 'wait' : 'pointer',
              fontSize: 11,
              fontWeight: 700,
              opacity: menuBadgesLoading ? 0.6 : 1,
            }}
            disabled={menuBadgesLoading}
            title="Recalcular badges del menú"
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {menuBadgesLoading ? 'Actualizando…' : 'Actualizar badges'}
              {adminMenuBadgeTotal(menuBadges) > 0 ? <AdminAlertDot title="Hay avisos pendientes" /> : null}
            </span>
          </button>
          {MENU_GROUPS.map(group => {
            const open = menuOpenGroupId === group.id;
            const groupBadgeSum = adminMenuGroupBadgeSum(menuBadges, group.items);
            const groupHasBadge = groupBadgeSum > 0;
            return (
              <div key={group.id} style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={
                    groupHasBadge
                      ? `${group.title} (${groupBadgeSum} pendiente${groupBadgeSum === 1 ? '' : 's'} en el bloque)`
                      : group.title
                  }
                  onClick={() => setMenuOpenGroupId(prev => (prev === group.id ? null : group.id))}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    marginBottom: open ? 4 : 2,
                    borderRadius: 10,
                    border: '0.5px solid rgba(255,255,255,0.08)',
                    background: open ? 'rgba(200,168,75,0.07)' : 'rgba(255,255,255,0.03)',
                    color: open ? C.gold : 'rgba(255,255,255,0.65)',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  title={open ? 'Plegar bloque' : 'Desplegar bloque'}
                >
                  <span style={{ width: 12, flexShrink: 0, fontSize: 9, opacity: 0.75 }} aria-hidden>
                    {open ? '▾' : '▸'}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <span style={{ marginRight: 6 }}>{group.emoji}</span>
                    {group.title}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {groupHasBadge ? (
                      <AdminAlertDot title={`Pendientes en ${group.title}: ${groupBadgeSum}`} />
                    ) : null}
                    {groupHasBadge ? <AdminAlertCount count={groupBadgeSum} /> : null}
                  </span>
                </button>
                {open ? (
                  <div style={{ paddingLeft: 6 }}>
                    {group.items.map(item => {
                      const itemBadgeCount = adminMenuItemBadgeCount(menuBadges, item.id);
                      return (
                      <button
                        key={item.id}
                        type="button"
                        title={item.hint || item.label}
                        onClick={() => {
                          setActive(item.id);
                          setBannerError('');
                          setMenuOpenGroupId(group.id);
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '9px 12px',
                          marginBottom: 3,
                          borderRadius: 10,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: active === item.id ? 700 : 500,
                          color: active === item.id ? C.gold : 'rgba(255,255,255,0.55)',
                          background: active === item.id ? 'rgba(200,168,75,0.12)' : 'transparent',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            {itemBadgeCount > 0 ? (
                              <AdminAlertDot title={`${itemBadgeCount} pendiente(s)`} />
                            ) : null}
                            <span>{item.label}</span>
                          </span>
                          <AdminAlertCount count={itemBadgeCount} />
                        </div>
                      </button>
                    );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </aside>
        <main style={{ flex: 1, padding: '20px 24px 40px', overflowY: 'auto' }}>
          <div style={{ margin: '0 0 6px', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px 12px' }}>
            <h1
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: C.gold,
                margin: 0,
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              {activePendingCount > 0 ? (
                <AdminAlertDot title={`${activePendingCount} pendiente(s) en esta sección`} size={10} />
              ) : null}
              {mainTitle}
              {activePendingCount > 0 ? <AdminAlertCount count={activePendingCount} /> : null}
            </h1>
            {mainHint ? (
              <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.42)', lineHeight: 1.4, maxWidth: 560 }}>
                {mainHint}
              </span>
            ) : null}
          </div>
          <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(200,168,75,0.35), transparent)', marginBottom: 18, maxWidth: 200 }} />
          {bannerError ? (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 10, background: 'rgba(226,75,74,0.12)', border: '0.5px solid rgba(226,75,74,0.25)', color: C.danger, fontSize: 12 }}>
              {bannerError}
            </div>
          ) : null}
          {bannerSuccess ? (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 10, background: 'rgba(76,175,80,0.12)', border: '0.5px solid rgba(76,175,80,0.28)', color: C.green, fontSize: 12 }}>
              {bannerSuccess}
            </div>
          ) : null}
          {active === 'dashboard' ? (
            <AdminDashboard user={user} onError={setBannerError} onGo={setActive} menuBadges={menuBadges} />
          ) : null}
          {active === 'partners' ? (
            <PartnersAdmin user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} menuBadges={menuBadges} />
          ) : null}
          {active === 'partner_registro' ? (
            <AdminPartnerRegistroPanel
              user={user}
              onError={setBannerError}
              onBadgesRefresh={loadMenuBadges}
              menuBadges={menuBadges}
              onGoPartners={() => setActive('partners')}
            />
          ) : null}
          {active === 'maintenance' ? (
            <MaintenanceAdmin onError={reportAdminError} onSaved={reportAdminSaved} />
          ) : null}
          {active === 'noticias' ? (
            <AdminNoticiasPanel
              user={user}
              onError={reportAdminError}
              onSuccess={reportAdminSaved}
              onBadgesRefresh={loadMenuBadges}
              menuBadges={menuBadges}
            />
          ) : null}
          {active === 'comunidad' ? (
            <AdminComunidadPanel
              onError={reportAdminError}
              onSuccess={reportAdminSaved}
              user={user}
              onBadgesRefresh={loadMenuBadges}
              menuBadges={menuBadges}
              initialTab={initialComunidadTab}
            />
          ) : null}
          {active === 'publicidad' ? (
            <AdminAdsDrafts
              user={user}
              onError={setBannerError}
              onSuccess={setBannerSuccess}
              onBadgesRefresh={loadMenuBadges}
              menuBadges={menuBadges}
            />
          ) : null}
          {active === 'admin_mensajes' ? <AdminPartnerMessages user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} /> : null}
          {active === 'usuarios' ? <AdminUsuariosPanel user={user} onError={setBannerError} /> : null}
          {active === 'grooves' ? <AdminGroovesPanel user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} /> : null}
          {active === 'sorteos' ? <AdminSorteosPanel user={user} onError={setBannerError} /> : null}
          {active === 'redes_cola' ? (
            <AdminSocialQueuePanel
              onError={setBannerError}
              onSuccess={setBannerSuccess}
              onBadgesRefresh={loadMenuBadges}
              menuBadges={menuBadges}
              onOpenSocialKit={({ contentType, contentId, titleHint }) =>
                setSocialKitGlobalOpen({ contentType, contentId, titleHint: titleHint || '' })
              }
            />
          ) : null}
          {active === 'estadisticas' ? <AdminEstadisticasPanel onError={setBannerError} /> : null}
          {active === 'emails' ? (
            <AdminEmailsPanel user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} menuBadges={menuBadges} />
          ) : null}
          {active === 'auditoria' ? <AdminAuditoriaPanel onError={setBannerError} /> : null}
          {active === 'listings' ? (
            <AdminListingsPanel
              user={user}
              onError={setBannerError}
              onBadgesRefresh={loadMenuBadges}
              menuBadges={menuBadges}
              onGoAdminSection={setActive}
              onViewListingFicha={handleViewListingFicha}
              fichaLoadingId={fichaLoadingId}
            />
          ) : null}
          {active === 'valoraciones' ? (
            <AdminValoracionesPanel user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} menuBadges={menuBadges} />
          ) : null}
          {active === 'subastas' ? (
            <AdminSubastasPanel
              onError={setBannerError}
              onBadgesRefresh={loadMenuBadges}
              onViewListingFicha={handleViewListingFicha}
              fichaLoadingId={fichaLoadingId}
            />
          ) : null}
          {active === 'transacciones' ? <AdminTransaccionesPanel onError={setBannerError} /> : null}
          {active === 'reportes' ? (
            <AdminReportesPanel user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} menuBadges={menuBadges} />
          ) : null}
          {active === 'notificaciones' ? <AdminNotificacionesPanel onError={setBannerError} /> : null}
          {active === 'eventos' ? (
            <AdminEventosPanel user={user} onError={setBannerError} onBadgesRefresh={loadMenuBadges} menuBadges={menuBadges} />
          ) : null}
          {active === 'directorio' ? <AdminDirectorioPartnersPanel onError={setBannerError} /> : null}
          {active === 'config' ? <AdminConfigPanel onError={setBannerError} /> : null}
          {isPlaceholder ? <Placeholder /> : null}
        </main>
      </div>
      <AdminSocialKitModal
        open={!!socialKitGlobalOpen}
        onClose={() => setSocialKitGlobalOpen(null)}
        contentType={socialKitGlobalOpen?.contentType ?? 'listing'}
        contentId={socialKitGlobalOpen?.contentId ?? ''}
        titleHint={socialKitGlobalOpen?.titleHint ?? ''}
        onError={setBannerError}
      />
    </div>
  );
}
